/**
 * IssueTerrain3D — Panoramic Mountain Range Visualization v2
 *
 * Visual encoding:
 *   Height   → HEAT score (exponential scale: HEAT 20 → 6 units, HEAT 70 → 42 units)
 *   Width    → sqrt(all-time evidence count) → base radius 3–16 world units (preserves issue memory)
 *   Pulse    → heat_delta animation (positive = glowing, negative = dimming)
 *   Color    → sentiment (red=negative, cyan=positive, grey=neutral)
 *   Labels   → compact rank badges always visible; full card on hover/select only
 *   Lines    → CatmullRom arcs between spillover-connected peaks
 *
 * No drei dependency — OrbitControls implemented inline.
 * Default camera: elevated 45° view looking across the full range.
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { buildMountainGeometry, buildRidgeHeightfield, hash32, IssueStamp, RidgePeak, SaddleEdge } from '../utils/perlinTerrain';
import { PerlinNoise } from '../utils/perlinNoise';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration Constants
// ─────────────────────────────────────────────────────────────────────────────
/** Camera height multiplier: camY = maxHeat * CAMERA_HEIGHT_MULTIPLIER
 *  Ensures full peak visibility for all HEAT scores (tested up to HEAT=100)
 *  Increase to zoom out more, decrease to zoom in (minimum 0.5 recommended) */
const CAMERA_HEIGHT_MULTIPLIER = 1.20;

/** Presentation mode orbit animation speeds (used in PresentationCameraController) */
const PRESENTATION_ORBIT_TARGET_LERP_SPEED = 2.0;  // How fast camera target moves to issue (delta multiplier)
const PRESENTATION_ORBIT_POSITION_LERP_SPEED = 1.6; // How fast camera position interpolates (delta multiplier)
const PRESENTATION_ORBIT_ANGLE_SPEED = 0.14;       // Orbit rotation speed in radians/second (~8°/s)

/** Presentation mode zoom control settings */
const PRESENTATION_ZOOM_MIN_RADIUS = 20;   // Closest zoom distance (units)
const PRESENTATION_ZOOM_MAX_RADIUS = 500;  // Farthest zoom distance (units)
const PRESENTATION_ZOOM_MOUSE_SENSITIVITY = 0.12;  // Mouse wheel zoom sensitivity
const PRESENTATION_ZOOM_TOUCH_SENSITIVITY = 0.08;  // Touch pinch zoom sensitivity (slower for comfort)
const PRESENTATION_INITIAL_ORBIT_RADIUS = 90;      // Default orbit distance on presentation start

// ─────────────────────────────────────────────────────────────────────────────
// API Types
// ─────────────────────────────────────────────────────────────────────────────
interface TerrainPeak {
  issue_id: string;
  title: string;
  rank: number;
  heat_now: number;
  heat_prev: number | null;
  heat_delta: number | null;
  is_emerging: boolean;
  evidence_count_now: number;
  evidence_velocity?: number;
  sentiment_state: string;
  spillover_degree: number;
  spillover_percentage?: number;
  stakeholder_count?: number;
  window: string;
  scope: string;
}

interface StakeholderBeaconData {
  stakeholder_id: string;
  name: string;
  category: string;
  organization: string;
  profile_image: string;
  role: string;
  influence_level: number;
  interest_level: number;
  salience: number;
  salience_norm: number;
  beacon_scale: number;
  colour: string;
  last_updated?: string;
  last_change_reason?: string;
}

interface TerrainConnection {
  from_rank: number;
  to_rank: number;
  from_id: string;
  to_id: string;
  shared_stakeholders?: number;
  weight?: number;       // 0.0–1.0 normalised
  thickness?: number;    // 0.04–0.18
  opacity?: number;      // 0.25–0.75 (base; dimmed further when not focused)
}

interface TerrainData {
  client_id: string;
  peaks: TerrainPeak[];
  connections?: TerrainConnection[];
  summary: {
    total_issues: number;
    peaks_returned: number;
    avg_heat: number;
    max_heat: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Presentation Mode types
// ─────────────────────────────────────────────────────────────────────────────
interface PresentationChapter {
  issue_id: string;
  headline: string;
  summary: string;
  why_it_matters: string;
  watch_for: string;
}
interface PresentationScript {
  intro: { headline: string; text: string };
  chapters: PresentationChapter[];
  closing: { headline: string; text: string };
}
interface PresentationResponse {
  client_id: string;
  cached: boolean;
  issue_count: number;
  issues: Array<{ issue_id: string; title: string; heat_now: number }>;
  script: PresentationScript;
}
type PresentationPhase = 'idle' | 'loading' | 'playing' | 'paused';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function sentimentColor(state: string): THREE.Color {
  const s = (state || '').toLowerCase();
  if (s === 'negative') return new THREE.Color(0xff4444);
  if (s === 'positive') return new THREE.Color(0x00e5ff);
  return new THREE.Color(0x9090a0);
}

function sentimentHex(state: string): string {
  const s = (state || '').toLowerCase();
  if (s === 'negative') return '#ff4444';
  if (s === 'positive') return '#00e5ff';
  return '#9090a0';
}

/** Map peaks to IssueStamp[] with left-to-right X placement, sorted high→low HEAT */
function peaksToStamps(
  peaks: TerrainPeak[],
  seed: number,
  maxHeat: number
): { stamps: IssueStamp[]; positions: THREE.Vector3[] } {
  const n = peaks.length;
  if (n === 0) return { stamps: [], positions: [] };

  // Sort by heat descending so tallest mountains are most prominent
  const sorted = [...peaks].sort((a, b) => b.heat_now - a.heat_now);

  const SPACING = 16;
  const totalW = (n - 1) * SPACING;
  const stamps: IssueStamp[] = [];
  const positions: THREE.Vector3[] = [];
  // Perlin noise instance for the mountain range spine (shared across all peaks)
  const spineNoise = new PerlinNoise(seed ^ 0xdeadbeef);
  sorted.forEach((peak, i) => {
    const color = sentimentColor(peak.sentiment_state);
    stamps.push({
      id: peak.issue_id,
      x: 0, z: 0,
      heatNow: peak.heat_now,   // raw HEAT score 0-100; buildMountainGeometry applies power-curve mapping
      evidenceCountNow: peak.evidence_count_now,
      sentimentState: peak.sentiment_state,
      spilloverPercentage: peak.spillover_percentage ?? 0,
      stakeholderCount: peak.stakeholder_count ?? 0,
      color,
      heatDelta: peak.heat_delta ?? null,
    });
    const x = -totalW / 2 + i * SPACING;
    // Smooth Perlin spine: each peak follows a gently winding path along Z.
    // The noise is sampled at the peak's normalised X position so adjacent
    // peaks are correlated (smooth curve) rather than independently random.
    const spineT = n > 1 ? i / (n - 1) : 0.5;
    const spineZ = spineNoise.noise(spineT * 3.5, 0) * 6.0;  // ±6 world units
    positions.push(new THREE.Vector3(x, 0, spineZ));
  });

  return { stamps, positions };
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline OrbitControls (no drei) — mouse + touch + reset
// ─────────────────────────────────────────────────────────────────────────────
function CameraControls({ target, focusCamRef, isFocusMode, isPresenting, presenterCamRef, resetRef, defaultCam }: {
  target: THREE.Vector3;
  focusCamRef?: React.MutableRefObject<{ active: boolean; targetPos: THREE.Vector3; orbitAngle: number; userInteracting: boolean; focusZoomRadius: number }>;
  isFocusMode?: boolean;
  isPresenting?: boolean;
  presenterCamRef?: React.MutableRefObject<PresenterCamState>;
  resetRef?: React.MutableRefObject<(() => void) | null>;
  defaultCam?: { position: THREE.Vector3; target: THREE.Vector3 };
}) {
  const { camera, gl } = useThree();
  const isDragging = useRef(false);
  const isRightDragging = useRef(false);
  const prevMouse = useRef({ x: 0, y: 0 });
  const targetRef = useRef(target.clone());
  // Keep a ref so the stale-closure inside useEffect always reads the live value
  const isFocusModeRef = useRef(isFocusMode ?? false);
  useEffect(() => { isFocusModeRef.current = isFocusMode ?? false; }, [isFocusMode]);
  const isPresentingRef = useRef(isPresenting ?? false);
  useEffect(() => { isPresentingRef.current = isPresenting ?? false; }, [isPresenting]);

  // ── Smooth reset animation state ───────────────────────────────────────────
  const resetAnim = useRef<{
    active: boolean;
    fromPos: THREE.Vector3;
    fromTarget: THREE.Vector3;
    toPos: THREE.Vector3;
    toTarget: THREE.Vector3;
    t: number;
  } | null>(null);

  // Wire the reset function into the ref so the button can call it
  useEffect(() => {
    if (!resetRef) return;
    resetRef.current = () => {
      if (!defaultCam) return;
      resetAnim.current = {
        active: true,
        fromPos: camera.position.clone(),
        fromTarget: targetRef.current.clone(),
        toPos: defaultCam.position.clone(),
        toTarget: defaultCam.target.clone(),
        t: 0,
      };
    };
  }, [camera, resetRef, defaultCam]);

  // Animate the reset lerp each frame
  useFrame(() => {
    const anim = resetAnim.current;
    if (!anim || !anim.active) return;
    anim.t = Math.min(1, anim.t + 0.045); // ~22 frames to complete (~0.37s at 60fps)
    const ease = 1 - Math.pow(1 - anim.t, 3); // cubic ease-out
    camera.position.lerpVectors(anim.fromPos, anim.toPos, ease);
    targetRef.current.lerpVectors(anim.fromTarget, anim.toTarget, ease);
    camera.lookAt(targetRef.current);
    if (anim.t >= 1) anim.active = false;
  });

  useEffect(() => {
    const canvas = gl.domElement;

    // ── Mouse handlers ────────────────────────────────────────────────────────
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 2) isRightDragging.current = true;
      else isDragging.current = true;
      prevMouse.current = { x: e.clientX, y: e.clientY };
      // Cancel any in-progress reset animation on manual interaction
      if (resetAnim.current) resetAnim.current.active = false;
    };

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - prevMouse.current.x;
      const dy = e.clientY - prevMouse.current.y;
      prevMouse.current = { x: e.clientX, y: e.clientY };

      // In Focus Mode the FocusCameraController owns the camera — don't fight it
      if (isFocusModeRef.current) return;

      if (isDragging.current) {
        const offset = camera.position.clone().sub(targetRef.current);
        const r = offset.length();
        const theta = Math.atan2(offset.z, offset.x) - dx * 0.007;
        const phi = Math.max(
          0.03,
          Math.min(Math.PI * 0.49, Math.acos(Math.max(-1, Math.min(1, offset.y / r))) + dy * 0.007)
        );
        camera.position.set(
          targetRef.current.x + r * Math.sin(phi) * Math.cos(theta),
          targetRef.current.y + r * Math.cos(phi),
          targetRef.current.z + r * Math.sin(phi) * Math.sin(theta)
        );
        camera.lookAt(targetRef.current);
      } else if (isRightDragging.current) {
        const panSpeed = 0.18;
        const right = new THREE.Vector3();
        camera.getWorldDirection(right);
        right.cross(camera.up).normalize();
        const panVec = right.clone().multiplyScalar(-dx * panSpeed);
        panVec.addScaledVector(camera.up, dy * panSpeed);
        camera.position.add(panVec);
        targetRef.current.add(panVec);
        camera.lookAt(targetRef.current);
      }
    };

    const onMouseUp = () => {
      isDragging.current = false;
      isRightDragging.current = false;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (resetAnim.current) resetAnim.current.active = false;
      if (isFocusModeRef.current && focusCamRef?.current?.active) {
        // In focus mode: adjust the orbit radius so FocusCameraController picks it up
        const current = focusCamRef.current.focusZoomRadius || 35;
        focusCamRef.current.focusZoomRadius = Math.max(15, Math.min(400, current + e.deltaY * 0.12));
      } else if (isPresentingRef.current && presenterCamRef?.current?.active) {
        // In presentation mode: adjust orbitRadius so PresentationCameraController picks it up
        const current = presenterCamRef.current.orbitRadius || PRESENTATION_INITIAL_ORBIT_RADIUS;
        presenterCamRef.current.orbitRadius = Math.max(PRESENTATION_ZOOM_MIN_RADIUS, Math.min(PRESENTATION_ZOOM_MAX_RADIUS, current + e.deltaY * PRESENTATION_ZOOM_MOUSE_SENSITIVITY));
      } else {
        const offset = camera.position.clone().sub(targetRef.current);
        const dist = Math.max(8, Math.min(800, offset.length() + e.deltaY * 0.12));
        offset.normalize().multiplyScalar(dist);
        camera.position.copy(targetRef.current.clone().add(offset));
        camera.lookAt(targetRef.current);
      }
    };

    const onContextMenu = (e: Event) => e.preventDefault();

    // ── Touch handlers ────────────────────────────────────────────────────────
    // Tracks previous touch state for delta computation
    let _prevTouchX = 0, _prevTouchY = 0;
    let _prevPinchDist = 0;
    let _prevPanCX = 0, _prevPanCY = 0;
    let _touchCount = 0;
    // Double-tap detection
    let _lastTapTime = 0;
    let _lastTapX = 0, _lastTapY = 0;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (resetAnim.current) resetAnim.current.active = false;
      _touchCount = e.touches.length;
      if (_touchCount === 1) {
        _prevTouchX = e.touches[0].clientX;
        _prevTouchY = e.touches[0].clientY;
      } else if (_touchCount === 2) {
        const dx2 = e.touches[1].clientX - e.touches[0].clientX;
        const dy2 = e.touches[1].clientY - e.touches[0].clientY;
        _prevPinchDist = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        _prevPanCX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        _prevPanCY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (isFocusModeRef.current) return;
      _touchCount = e.touches.length;

      if (_touchCount === 1) {
        // Single finger: orbit
        const dx = e.touches[0].clientX - _prevTouchX;
        const dy = e.touches[0].clientY - _prevTouchY;
        _prevTouchX = e.touches[0].clientX;
        _prevTouchY = e.touches[0].clientY;
        const offset = camera.position.clone().sub(targetRef.current);
        const r = offset.length();
        const theta = Math.atan2(offset.z, offset.x) - dx * 0.007;
        const phi = Math.max(
          0.03,
          Math.min(Math.PI * 0.49, Math.acos(Math.max(-1, Math.min(1, offset.y / r))) + dy * 0.007)
        );
        camera.position.set(
          targetRef.current.x + r * Math.sin(phi) * Math.cos(theta),
          targetRef.current.y + r * Math.cos(phi),
          targetRef.current.z + r * Math.sin(phi) * Math.sin(theta)
        );
        camera.lookAt(targetRef.current);

      } else if (_touchCount === 2) {
        // Two fingers: pinch-zoom + two-finger pan
        const dx2 = e.touches[1].clientX - e.touches[0].clientX;
        const dy2 = e.touches[1].clientY - e.touches[0].clientY;
        const newDist = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        const newCX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const newCY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        // Pinch zoom
        const distDelta = _prevPinchDist - newDist;
        if (Math.abs(distDelta) > 0.5) {
          if (isPresentingRef.current && presenterCamRef?.current?.active) {
            // In presentation mode: adjust orbitRadius with touch sensitivity
            const current = presenterCamRef.current.orbitRadius || PRESENTATION_INITIAL_ORBIT_RADIUS;
            presenterCamRef.current.orbitRadius = Math.max(PRESENTATION_ZOOM_MIN_RADIUS, Math.min(PRESENTATION_ZOOM_MAX_RADIUS, current + distDelta * PRESENTATION_ZOOM_TOUCH_SENSITIVITY));
          } else {
            // Standard mode: adjust camera distance
            const offset = camera.position.clone().sub(targetRef.current);
            const dist = Math.max(8, Math.min(800, offset.length() + distDelta * 0.35));
            offset.normalize().multiplyScalar(dist);
            camera.position.copy(targetRef.current.clone().add(offset));
            camera.lookAt(targetRef.current);
          }
        }
        _prevPinchDist = newDist;

        // Two-finger pan
        const panDX = newCX - _prevPanCX;
        const panDY = newCY - _prevPanCY;
        if (Math.abs(panDX) > 0.1 || Math.abs(panDY) > 0.1) {
          const panSpeed = 0.22;
          const right = new THREE.Vector3();
          camera.getWorldDirection(right);
          right.cross(camera.up).normalize();
          const panVec = right.clone().multiplyScalar(-panDX * panSpeed);
          panVec.addScaledVector(camera.up, panDY * panSpeed);
          camera.position.add(panVec);
          targetRef.current.add(panVec);
          camera.lookAt(targetRef.current);
        }
        _prevPanCX = newCX;
        _prevPanCY = newCY;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      _touchCount = e.touches.length;
      // Double-tap detection → fire synthetic click for peak selection
      if (e.changedTouches.length === 1) {
        const now = Date.now();
        const tx = e.changedTouches[0].clientX;
        const ty = e.changedTouches[0].clientY;
        const tapDist = Math.sqrt(Math.pow(tx - _lastTapX, 2) + Math.pow(ty - _lastTapY, 2));
        if (now - _lastTapTime < 300 && tapDist < 20) {
          // Double-tap: fire a dblclick event to trigger Focus Mode
          const clickEvt = new MouseEvent('dblclick', {
            bubbles: true, cancelable: true,
            clientX: tx, clientY: ty,
          });
          canvas.dispatchEvent(clickEvt);
          _lastTapTime = 0; // reset so triple-tap doesn't re-fire
        } else {
          _lastTapTime = now;
          _lastTapX = tx;
          _lastTapY = ty;
        }
      }
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [camera, gl]);

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Camera initializer — elevated 45° view from the side
// ─────────────────────────────────────────────────────────────────────────────
function CameraSetup({ peakCount, maxHeat }: { peakCount: number; maxHeat: number }) {
  const { camera } = useThree();
  useEffect(() => {
    const totalW = Math.max((peakCount - 1) * 13, 60);
    // Baked preferred view: ~3 scrolls closer + ~6cm rightward pan from centred default.
    // Pan right (user's right) shifts camera & target ~41 world units toward negative X
    // (toward the high-HEAT peaks which are at the left/negative end of the ridge).
    const PAN_OFFSET = -75;                            // world units toward high-HEAT peaks
    const camX = PAN_OFFSET;
    const camY = Math.max(maxHeat * 0.45, 30);         // elevated (~30-50 units)
    const camZ = totalW * 0.46;                        // 3 scrolls closer than centred default
    camera.position.set(camX, camY, camZ);
    (camera as THREE.PerspectiveCamera).fov = 58;
    camera.lookAt(PAN_OFFSET, 0, 0);                   // look-at tracks the pan offset
    camera.updateProjectionMatrix();
  }, [peakCount, maxHeat]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ground plane
// ─────────────────────────────────────────────────────────────────────────────
function GroundPlane({ width }: { width: number }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
      <planeGeometry args={[width + 100, 120]} />
      <meshStandardMaterial color={0x080d18} roughness={1} metalness={0} />
    </mesh>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ridge Mesh — continuous terrain beneath the mountain range
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Stakeholder Beacons — appear around a pinned mountain when selectedId is set
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical role → hex colour — must match backend _ROLE_COLOURS */
const ROLE_COLOURS: Record<string, string> = {
  'Supporter':      '#22c55e',
  'Opponent':       '#ef4444',
  'Decision Maker': '#f59e0b',
  'Influencer':     '#8b5cf6',
  'Affected Party': '#06b6d4',
  'Neutral':        '#94a3b8',
};

interface StakeholderBeaconsProps {
  beacons: StakeholderBeaconData[];
  /** World-space position of the pinned mountain base */
  mountainPos: THREE.Vector3;
  /** Base radius of the pinned mountain (for placement ring radius) */
  baseRadius: number;
  /** Peak height of the pinned mountain — used to elevate beacons above the foothills */
  peakY: number;
  onBeaconHover: (beacon: StakeholderBeaconData, sx: number, sy: number, pinned?: boolean) => void;
  onBeaconOut: () => void;
}

function StakeholderBeacons({ beacons, mountainPos, baseRadius, peakY, onBeaconHover, onBeaconOut }: StakeholderBeaconsProps) {
  if (beacons.length === 0) return null;
  const { camera, size } = useThree();
  const count = beacons.length;
  // Place beacons in an arc around the mountain shoulder.
  // Ring radius = 1.4× the mountain base radius so beacons sit just outside the foothills.
  const ringRadius = baseRadius * 0.85;
  const baseY = peakY * 0.30;
  // Spread beacons evenly across a 270° arc (front-facing, avoids the back wall)
  const arcStart  = -Math.PI * 0.67;
  const arcEnd    = Math.PI  * 0.67;
  const arcSpan   = arcEnd - arcStart;
  return (
    <>
      {beacons.map((b, idx) => {
        const angle = count === 1
          ? 0
          : arcStart + (idx / (count - 1)) * arcSpan;
        const bx = mountainPos.x + Math.sin(angle) * ringRadius;
        const bz = mountainPos.z + Math.cos(angle) * ringRadius;
        // Beacon height: min 1.2 world units, max 4.5 world units
        const beaconH = 1.2 + b.beacon_scale * 3.3;
        const colour  = ROLE_COLOURS[b.role] ?? ROLE_COLOURS['Neutral'];
        return (
          <group
            key={b.stakeholder_id}
            position={[bx, baseY, bz]}
            onPointerOver={(e) => {
              e.stopPropagation();
              // Project the beacon cap position to screen space
              const worldPos = new THREE.Vector3(bx, baseY + beaconH + 0.3, bz);
              worldPos.project(camera);
              const sx = (worldPos.x * 0.5 + 0.5) * size.width;
              const sy = (-worldPos.y * 0.5 + 0.5) * size.height;
              onBeaconHover(b, sx, sy);
            }}
            onPointerOut={(e) => { e.stopPropagation(); onBeaconOut(); }}
            onClick={(e) => {
              e.stopPropagation();
              // On touch: tap pins the tooltip (same projection as hover)
              const worldPos = new THREE.Vector3(bx, baseY + beaconH + 0.3, bz);
              worldPos.project(camera);
              const sx = (worldPos.x * 0.5 + 0.5) * size.width;
              const sy = (-worldPos.y * 0.5 + 0.5) * size.height;
              onBeaconHover(b, sx, sy, true /* pinned */);
            }}
          >
            {/* Vertical beacon pillar */}
            <mesh position={[0, beaconH / 2, 0]} castShadow>
              <cylinderGeometry args={[0.18, 0.28, beaconH, 8]} />
              <meshStandardMaterial
                color={colour}
                emissive={colour}
                emissiveIntensity={0.45}
                roughness={0.4}
                metalness={0.2}
              />
            </mesh>
            {/* Glowing cap sphere */}
            <mesh position={[0, beaconH + 0.22, 0]}>
              <sphereGeometry args={[0.32, 12, 8]} />
              <meshStandardMaterial
                color={colour}
                emissive={colour}
                emissiveIntensity={0.9}
                roughness={0.2}
                metalness={0.1}
              />
            </mesh>
            {/* Thin base ring for visual grounding */}
            <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.32, 0.52, 16]} />
              <meshBasicMaterial color={colour} transparent opacity={0.55} side={THREE.DoubleSide} />
            </mesh>
          </group>
        );
      })}
    </>
  );
}

interface RidgeMeshProps {
  ridgePeaks: RidgePeak[];
  seed: number;
  saddleEdges?: SaddleEdge[];
}
function RidgeMesh({ ridgePeaks, seed, saddleEdges = [] }: RidgeMeshProps) {
  const geometry = useMemo(() => {
    if (ridgePeaks.length === 0) return null;
    const hf = buildRidgeHeightfield(ridgePeaks, seed, 120, 40, 30, 80, saddleEdges);
    const { heights, cols, rows, worldW, worldD, originX, originZ } = hf;

    const positions: number[] = [];
    const normals:   number[] = [];
    const colors:    number[] = [];
    const indices:   number[] = [];

    // Build vertices
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const wx = originX + (col / (cols - 1)) * worldW;
        const wz = originZ + (row / (rows - 1)) * worldD;
        const wy = heights[row * cols + col];
        positions.push(wx, wy, wz);
        normals.push(0, 1, 0); // placeholder, recomputed below

        // Colour: blend dark base with the nearest peak's sentiment colour,
        // weighted by proximity so foothills take on the peak's hue.
        const BASE_COL = new THREE.Color(0x0d1a2e);
        let bestDist = Infinity;
        let bestColor = new THREE.Color(0x2a3a5a);
        for (const p of ridgePeaks) {
          const dx = wx - p.wx;
          const dz = wz - p.wz;
          const d = Math.sqrt(dx * dx + dz * dz);
          if (d < bestDist) { bestDist = d; bestColor = (p as any).color ?? bestColor; }
        }
        const blend = Math.max(0, 1 - bestDist / (Math.max(...ridgePeaks.map(p => p.baseRadius)) * 2.5));
        const c = BASE_COL.clone().lerp(bestColor, blend * 0.35);
        colors.push(c.r, c.g, c.b);
      }
    }

    // Recompute smooth normals via central differences
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const idx = row * cols + col;
        const hL = col > 0        ? heights[row * cols + col - 1] : heights[idx];
        const hR = col < cols - 1 ? heights[row * cols + col + 1] : heights[idx];
        const hD = row > 0        ? heights[(row - 1) * cols + col] : heights[idx];
        const hU = row < rows - 1 ? heights[(row + 1) * cols + col] : heights[idx];
        const dx = worldW / (cols - 1);
        const dz = worldD / (rows - 1);
        const nx = -(hR - hL) / (2 * dx);
        const nz = -(hU - hD) / (2 * dz);
        const len = Math.sqrt(nx * nx + 1 + nz * nz);
        normals[idx * 3]     = nx / len;
        normals[idx * 3 + 1] = 1  / len;
        normals[idx * 3 + 2] = nz / len;
      }
    }

    // Build triangle indices
    for (let row = 0; row < rows - 1; row++) {
      for (let col = 0; col < cols - 1; col++) {
        const a = row * cols + col;
        const b = a + 1;
        const c = a + cols;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('normal',   new THREE.Float32BufferAttribute(normals,   3));
    geo.setAttribute('color',    new THREE.Float32BufferAttribute(colors,    3));
    geo.setIndex(indices);
    return geo;
  }, [ridgePeaks, seed, saddleEdges]);

  if (!geometry) return null;

  return (
    <mesh geometry={geometry} receiveShadow castShadow>
      <meshStandardMaterial
        vertexColors
        roughness={0.88}
        metalness={0.04}
        emissive={new THREE.Color(0x0d2035)}
        emissiveIntensity={0.6}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Momentum animation constants (tunable)
// ─────────────────────────────────────────────────────────────────────────────
const MAX_DELTA_ANIM      = 3.0;    // sanity cap: real-world daily deltas rarely exceed 3 HEAT points
const MAX_AMPLITUDE_FACTOR = 0.15;  // 15% of baseHeight maximum amplitude (recalibrated post-settling)
const BASE_SPEED           = 1.2;   // radians/second base pulse speed
const SPEED_MULTIPLIER_POS = 1.15;  // +15% speed for rising issues (heat_delta > 0)
const SPEED_MULTIPLIER_NEG = 0.85;  // -15% speed for falling issues (heat_delta < 0)

// ─────────────────────────────────────────────────────────────────────────────
// Single Mountain
// ─────────────────────────────────────────────────────────────────────────────
interface MountainProps {
  stamp: IssueStamp;
  position: THREE.Vector3;
  seed: number;
  isHighlighted: boolean;
  isPinned: boolean;
  isFaded: boolean;
  onDoubleClick: (id: string) => void;
  onHover: (id: string | null) => void;
  onClick: (id: string) => void;
}

function Mountain({ stamp, position, seed, isHighlighted, isPinned, isFaded, onHover, onClick, onDoubleClick }: MountainProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { geometry, peakY } = useMemo(
    () => buildMountainGeometry(stamp, seed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamp.id, stamp.heatNow, stamp.evidenceCountNow, stamp.sentimentState, seed]
  );
  // Use refs so the useFrame closure always reads the live prop values,
  // avoiding stale-closure bugs during isFocusMode re-render cycles.
  const isPinnedRef   = useRef(isPinned);
  const heatDeltaRef  = useRef(stamp.heatDelta);
  useEffect(() => { isPinnedRef.current  = isPinned; },        [isPinned]);
  useEffect(() => { heatDeltaRef.current = stamp.heatDelta; }, [stamp.heatDelta]);
  // ── Momentum animation ──────────────────────────────────────────────────────
  // Animates ALL mountains that have a non-null heatDelta.
  // Pinned mountains pulse at full amplitude; unpinned at 40% (passive scan cue).
  useFrame((state, delta) => {
    if (!meshRef.current) return;
    const pinned    = isPinnedRef.current;
    const heatDelta = heatDeltaRef.current;
    if (heatDelta === null || heatDelta === undefined) {
      // No delta data — smoothly lerp back to resting scale.
      meshRef.current.scale.y += (1 - meshRef.current.scale.y) * Math.min(delta * 8, 1);
      return;
    }
    const delta_anim    = Math.min(Math.abs(heatDelta), MAX_DELTA_ANIM);
    const baseAmplitude = (delta_anim / MAX_DELTA_ANIM) * MAX_AMPLITUDE_FACTOR;
    // Pinned: full amplitude; unpinned: 40% (subtle passive cue)
    const amplitude  = pinned ? baseAmplitude : baseAmplitude * 0.4;
    const speed      = BASE_SPEED * (heatDelta >= 0 ? SPEED_MULTIPLIER_POS : SPEED_MULTIPLIER_NEG);
    const t          = state.clock.getElapsedTime();
    meshRef.current.scale.y = 1 + Math.sin(t * speed) * amplitude;
  });

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.82,
        metalness: 0.08,
        side: THREE.DoubleSide,
        transparent: true,
      }),
    []
  );

  useEffect(() => {
    if (!meshRef.current) return;
    const mat = meshRef.current.material as THREE.MeshStandardMaterial;
    if (isHighlighted) {
      mat.emissive = stamp.color.clone().multiplyScalar(0.4);
      mat.emissiveIntensity = 1;
    } else {
      mat.emissive = new THREE.Color(0x000000);
      mat.emissiveIntensity = 0;
    }
    mat.opacity = isFaded ? 0.08 : 1.0;
  }, [isHighlighted, isFaded, stamp.color]);

  useEffect(() => () => { geometry.dispose(); material.dispose(); }, [geometry, material]);

  return (
    <group position={position}>
      <mesh
        ref={meshRef}
        geometry={geometry}
        material={material}
        castShadow
        receiveShadow
        onPointerOver={(e) => { e.stopPropagation(); onHover(stamp.id); }}
        onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
        onClick={(e) => { e.stopPropagation(); onClick(stamp.id); }}
        onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(stamp.id); }}
        onContextMenu={(e) => { e.stopPropagation(); onDoubleClick(stamp.id); }}
      />
      {/* Summit glow orb — radius encodes stakeholder count (log-scaled) */}
      {(() => {
        const sc = (stamp as any).stakeholderCount ?? 0;
        const orbR = Math.min(Math.max(0.35 + Math.log(sc + 1) * 0.28, 0.35), 1.8);
        return (
          <mesh position={[0, peakY, 0]}>
            <sphereGeometry args={[orbR, 12, 12]} />
            <meshStandardMaterial
              color={stamp.color}
              emissive={stamp.color}
              emissiveIntensity={isHighlighted ? 4 : 1.6}
              transparent
              opacity={0.9}
            />
          </mesh>
        );
      })()}
    </group>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Spillover arc
// ─────────────────────────────────────────────────────────────────────────────
interface ConnectionArcProps {
  fromPos: THREE.Vector3;
  toPos: THREE.Vector3;
  fromPeakY: number;
  toPeakY: number;
  /** Edge weight 0–1 (normalised shared_stakeholders). Default 0.5 */
  weight?: number;
  /** Whether this arc connects to the currently hovered/selected peak */
  isHighlighted?: boolean;
  /** Whether any peak is focused (dims non-highlighted arcs) */
  hasFocus?: boolean;
  /** In Focus Mode, hide arcs that are not part of the focus cluster */
  isFocusHidden?: boolean;
  /** Whether this arc is currently being hovered directly */
  isArcHovered?: boolean;
  /** Callbacks for arc hover tooltip */
  onArcHover?: (midpoint: THREE.Vector3) => void;
  onArcOut?: () => void;
}
function ConnectionArc({
  fromPos, toPos, fromPeakY, toPeakY,
  weight = 0.5, isHighlighted = false, hasFocus = false, isFocusHidden = false,
  isArcHovered = false, onArcHover, onArcOut,
}: ConnectionArcProps) {
  const curve = useMemo(() => {
    const p0 = new THREE.Vector3(fromPos.x, fromPos.y + fromPeakY, fromPos.z);
    const p3 = new THREE.Vector3(toPos.x, toPos.y + toPeakY, toPos.z);
    const midX = (p0.x + p3.x) / 2;
    const midZ = (p0.z + p3.z) / 2;
    const arcH = Math.max(fromPeakY, toPeakY) * 0.5 + 8;
    const p1 = new THREE.Vector3(midX, arcH + fromPeakY * 0.2, midZ - 2);
    const p2 = new THREE.Vector3(midX, arcH + toPeakY * 0.2, midZ + 2);
    return new THREE.CatmullRomCurve3([p0, p1, p2, p3]);
  }, [fromPos, toPos, fromPeakY, toPeakY]);

  // Thin tube for normal/highlighted state (radius 0.14)
  const thinTube = useMemo(
    () => new THREE.TubeGeometry(curve, 32, 0.14, 6, false),
    [curve]
  );
  // Thick tube for hovered state (radius 0.63) — pre-built, toggled by visibility
  const thickTube = useMemo(
    () => new THREE.TubeGeometry(curve, 32, 0.63, 8, false),
    [curve]
  );
  // Wide invisible tube for pointer hit detection (radius 1.0)
  const hitTube = useMemo(
    () => new THREE.TubeGeometry(curve, 24, 1.0, 4, false),
    [curve]
  );
  // midpoint must be computed before any early returns (Rules of Hooks)
  const midpoint = useMemo(() => curve.getPoint(0.5), [curve]);
  useEffect(() => () => { thinTube.dispose(); thickTube.dispose(); hitTube.dispose(); }, [thinTube, thickTube, hitTube]);

  if (isFocusHidden) return null;

  // Opacity: highlighted or arc-hovered → full; unfocused → dim; no focus → base
  const baseOpacity = 0.28 + weight * 0.45;   // 0.28–0.73
  const thinOpacity = isArcHovered    ? 0
                    : isHighlighted   ? Math.min(baseOpacity * 1.5, 0.9)
                    : hasFocus        ? baseOpacity * 0.18
                    : baseOpacity * 0.6;
  // Normal arc colour: muted amber; peak-highlighted: bright gold
  const thinColor = isHighlighted ? 0xffcc00 : 0xff9500;

  return (
    <group>
      {/* Normal / peak-highlighted arc — thin tube, unlit so always visible */}
      <mesh geometry={thinTube} visible={!isArcHovered}>
        <meshBasicMaterial
          color={thinColor}
          transparent
          opacity={thinOpacity}
          depthWrite={false}
        />
      </mesh>

      {/* Arc-hovered state — thick bright gold tube, unlit so it always shows */}
      <mesh geometry={thickTube} visible={isArcHovered}>
        <meshBasicMaterial
          color={0xffdd00}
          transparent
          opacity={0.95}
          depthWrite={false}
        />
      </mesh>

      {/* Invisible wide hit-target tube — pointer events only */}
      <mesh
        geometry={hitTube}
        onPointerOver={(e) => { e.stopPropagation(); onArcHover?.(midpoint); }}
        onPointerOut={(e) => { e.stopPropagation(); onArcOut?.(); }}
      >
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Summit position tracker (projects 3D → 2D for DOM labels)
// ─────────────────────────────────────────────────────────────────────────────
interface SummitTrackerProps {
  positions: THREE.Vector3[];
  peakYs: number[];
  onUpdate: (screenPositions: Array<{ x: number; y: number; visible: boolean }>) => void;
}

function SummitTracker({ positions, peakYs, onUpdate }: SummitTrackerProps) {
  const { camera, size } = useThree();
  const projVec = useMemo(() => new THREE.Vector3(), []);
  // Cache last-sent positions to avoid triggering React re-renders every frame
  const prevResults = useRef<Array<{ x: number; y: number; visible: boolean }>>([]);

  useFrame(() => {
    const results = positions.map((pos, i) => {
      projVec.set(pos.x, pos.y + (peakYs[i] ?? 6) + 1.5, pos.z);
      projVec.project(camera);
      const x = (projVec.x * 0.5 + 0.5) * size.width;
      const y = (-projVec.y * 0.5 + 0.5) * size.height;
      const visible = projVec.z < 1 && projVec.z > -1;
      return { x, y, visible };
    });
    // Only push a state update when something actually moved (>0.5px) or visibility changed
    const prev = prevResults.current;
    const changed = results.length !== prev.length || results.some((r, i) => {
      const p = prev[i];
      return !p || r.visible !== p.visible || Math.abs(r.x - p.x) > 0.5 || Math.abs(r.y - p.y) > 0.5;
    });
    if (changed) {
      prevResults.current = results;
      onUpdate(results);
    }
  });

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Focus Mode Camera Controller
// ─────────────────────────────────────────────────────────────────────────────
interface FocusCamState {
  active: boolean;
  targetPos: THREE.Vector3;
  orbitAngle: number;
  userInteracting: boolean;
  focusZoomRadius: number;
}
function FocusCameraController({
  focusCamRef,
  defaultTarget,
  isFocusMode,
}: {
  focusCamRef: React.MutableRefObject<FocusCamState>; // FocusCamState now includes focusZoomRadius
  defaultTarget: THREE.Vector3;
  isFocusMode: boolean;
}) {
  const { camera, gl } = useThree();
  const lerpTarget = useRef(new THREE.Vector3());
  const lerpPos    = useRef(new THREE.Vector3());
  const initialised = useRef(false);

  // Track user interaction to pause auto-orbit
  useEffect(() => {
    const canvas = gl.domElement;
    const onDown = () => { focusCamRef.current.userInteracting = true; };
    const onUp   = () => {
      // Resume auto-orbit after 2 seconds of inactivity
      setTimeout(() => { focusCamRef.current.userInteracting = false; }, 2000);
    };
    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
    };
  }, [gl, focusCamRef]);

  useFrame((_, delta) => {
    const state = focusCamRef.current;
    if (!state.active || !isFocusMode) return;

    // Initialise lerp targets on first frame
    if (!initialised.current) {
      lerpTarget.current.copy(state.targetPos);
      lerpPos.current.copy(camera.position);
      initialised.current = true;
    }

    // Smoothly move camera target toward the focus issue
    lerpTarget.current.lerp(state.targetPos, delta * 2.5);

    // Advance orbit angle only when user is not interacting
    if (!state.userInteracting) {
      state.orbitAngle += delta * 0.18; // ~0.18 rad/s = ~10°/s
    }

    // Orbit radius: use user-adjusted zoom radius if set, else pull in to ~60% of current distance
    const currentOffset = camera.position.clone().sub(lerpTarget.current);
    const currentDist   = currentOffset.length();
    const userRadius    = state.focusZoomRadius;
    const targetDist    = userRadius > 0 ? userRadius : Math.max(currentDist * 0.6, 35);
    // Initialise focusZoomRadius on first frame so subsequent scrolls have a baseline
    if (userRadius === 0) {
      state.focusZoomRadius = Math.max(currentDist * 0.6, 35);
    }

    const orbitX = lerpTarget.current.x + targetDist * Math.sin(state.orbitAngle) * 0.9;
    const orbitY = lerpTarget.current.y + targetDist * 0.55;
    const orbitZ = lerpTarget.current.z + targetDist * Math.cos(state.orbitAngle) * 0.9;

    lerpPos.current.lerp(new THREE.Vector3(orbitX, orbitY, orbitZ), delta * 1.8);
    camera.position.copy(lerpPos.current);
    camera.lookAt(lerpTarget.current);
  });

  // Reset initialised flag when focus mode exits
  useEffect(() => {
    if (!isFocusMode) {
      initialised.current = false;
      // Reset zoom radius so next focus session starts fresh
      focusCamRef.current.focusZoomRadius = 0;
    }
  }, [isFocusMode]);

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Presentation Mode Camera Controller
// Flies to each chapter's target issue, then slow-orbits while audio plays.
// User interaction (drag/scroll) interrupts the orbit; resumes after 3s idle.
// ─────────────────────────────────────────────────────────────────────────────
interface PresenterCamState {
  active: boolean;
  targetPos: THREE.Vector3;    // world-space look-at target
  orbitAngle: number;          // current orbit angle (radians)
  orbitRadius: number;         // distance from target
  userInteracting: boolean;    // true while user is dragging/scrolling
  resumeAt: number;            // timestamp (ms) when auto-orbit resumes
}

function PresentationCameraController({
  presenterCamRef,
  isPresenting,
}: {
  presenterCamRef: React.MutableRefObject<PresenterCamState>;
  isPresenting: boolean;
}) {
  const { camera, gl } = useThree();
  const lerpTarget = useRef(new THREE.Vector3());
  const lerpPos    = useRef(new THREE.Vector3());
  const initialised = useRef(false);

  // Interrupt orbit on user interaction
  useEffect(() => {
    if (!isPresenting) return;
    const canvas = gl.domElement;
    const onDown = () => {
      presenterCamRef.current.userInteracting = true;
      presenterCamRef.current.resumeAt = Date.now() + 3000; // resume after 3s
    };
    const onUp = () => {
      // resumeAt already set in onDown; just mark not actively dragging
    };
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('touchstart', onDown, { passive: true });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('touchstart', onDown);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [gl, isPresenting, presenterCamRef]);

  useFrame((_, delta) => {
    const state = presenterCamRef.current;
    if (!state.active || !isPresenting) return;

    // Initialise lerp targets on first frame
    if (!initialised.current) {
      lerpTarget.current.copy(state.targetPos);
      lerpPos.current.copy(camera.position);
      initialised.current = true;
    }

    // Resume auto-orbit after idle period
    if (state.userInteracting && Date.now() > state.resumeAt) {
      state.userInteracting = false;
    }

    // Smoothly fly target toward chapter's issue
    lerpTarget.current.lerp(state.targetPos, delta * PRESENTATION_ORBIT_TARGET_LERP_SPEED);

    // Advance orbit angle when not interrupted
    if (!state.userInteracting) {
      state.orbitAngle += delta * PRESENTATION_ORBIT_ANGLE_SPEED;
    }

    const r = state.orbitRadius;
    const orbitX = lerpTarget.current.x + r * Math.sin(state.orbitAngle) * 0.85;
    const orbitY = lerpTarget.current.y + r * 0.50;
    const orbitZ = lerpTarget.current.z + r * Math.cos(state.orbitAngle) * 0.85;

    lerpPos.current.lerp(new THREE.Vector3(orbitX, orbitY, orbitZ), delta * PRESENTATION_ORBIT_POSITION_LERP_SPEED);
    camera.position.copy(lerpPos.current);
    camera.lookAt(lerpTarget.current);
  });

  // Reset on exit
  useEffect(() => {
    if (!isPresenting) {
      initialised.current = false;
    }
  }, [isPresenting]);

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PresentationBeacons — like StakeholderBeacons but with an activeBeaconIdx
// that makes the active beacon glow brighter and auto-projects its tooltip.
// ─────────────────────────────────────────────────────────────────────────────
interface PresentationBeaconsProps {
  beacons: StakeholderBeaconData[];
  activeBeaconIdx: number | null;
  mountainPos: THREE.Vector3;
  baseRadius: number;
  peakY: number;
  onBeaconHover: (beacon: StakeholderBeaconData, sx: number, sy: number, pinned?: boolean) => void;
  onBeaconOut: () => void;
}

function PresentationBeacons({
  beacons, activeBeaconIdx, mountainPos, baseRadius, peakY,
  onBeaconHover, onBeaconOut,
}: PresentationBeaconsProps) {
  if (beacons.length === 0) return null;
  const { camera, size } = useThree();
  const count = beacons.length;
  const ringRadius = baseRadius * 0.85;
  const baseY = peakY * 0.30;
  const arcStart = -Math.PI * 0.67;
  const arcEnd   =  Math.PI * 0.67;
  const arcSpan  = arcEnd - arcStart;

  // When activeBeaconIdx changes, project that beacon's cap to screen and fire onBeaconHover.
  // We use a useEffect so it fires once per index change (not every frame).
  useEffect(() => {
    if (activeBeaconIdx === null || activeBeaconIdx >= beacons.length) {
      onBeaconOut();
      return;
    }
    const b = beacons[activeBeaconIdx];
    const angle = count === 1 ? 0 : arcStart + (activeBeaconIdx / (count - 1)) * arcSpan;
    const bx = mountainPos.x + Math.sin(angle) * ringRadius;
    const bz = mountainPos.z + Math.cos(angle) * ringRadius;
    const beaconH = 1.2 + b.beacon_scale * 3.3;
    const worldPos = new THREE.Vector3(bx, baseY + beaconH + 0.3, bz);
    worldPos.project(camera);
    const sx = (worldPos.x * 0.5 + 0.5) * size.width;
    const sy = (-worldPos.y * 0.5 + 0.5) * size.height;
    onBeaconHover(b, sx, sy);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBeaconIdx]);

  return (
    <>
      {beacons.map((b, idx) => {
        const angle = count === 1 ? 0 : arcStart + (idx / (count - 1)) * arcSpan;
        const bx = mountainPos.x + Math.sin(angle) * ringRadius;
        const bz = mountainPos.z + Math.cos(angle) * ringRadius;
        const beaconH = 1.2 + b.beacon_scale * 3.3;
        const colour  = ROLE_COLOURS[b.role] ?? ROLE_COLOURS['Neutral'];
        const isActive = idx === activeBeaconIdx;
        return (
          <group key={b.stakeholder_id} position={[bx, baseY, bz]}>
            {/* Vertical beacon pillar */}
            <mesh position={[0, beaconH / 2, 0]}>
              <cylinderGeometry args={[0.09, 0.14, beaconH, 8]} />
              <meshStandardMaterial
                color={colour}
                emissive={colour}
                emissiveIntensity={isActive ? 2.5 : 0.6}
                transparent
                opacity={isActive ? 1.0 : 0.55}
              />
            </mesh>
            {/* Beacon cap sphere */}
            <mesh position={[0, beaconH + 0.18, 0]}>
              <sphereGeometry args={[isActive ? 0.38 : 0.22, 10, 10]} />
              <meshStandardMaterial
                color={colour}
                emissive={colour}
                emissiveIntensity={isActive ? 5 : 1.2}
                transparent
                opacity={isActive ? 1.0 : 0.7}
              />
            </mesh>
          </group>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Arc tooltip camera capture — writes the live camera + size into refs so the
// DOM-side arc tooltip can project 3D midpoints to screen coordinates.
// ─────────────────────────────────────────────────────────────────────────────
function ArcTooltipCameraCapture({
  cameraRef, sizeRef,
}: {
  cameraRef: React.MutableRefObject<THREE.Camera | null>;
  sizeRef: React.MutableRefObject<{ width: number; height: number }>;
}) {
  const { camera, size } = useThree();
  useEffect(() => { cameraRef.current = camera; }, [camera, cameraRef]);
  useEffect(() => { sizeRef.current = size; }, [size, sizeRef]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scene (inside Canvas)
// ─────────────────────────────────────────────────────────────────────────────
interface SceneProps {
  peaks: TerrainPeak[];
  sortedPeaks: TerrainPeak[];
  connections: TerrainConnection[];
  clientId: string;
  window_: string;
  scope: string;
  hoveredId: string | null;
  selectedId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
  onSummitPositions: (pos: Array<{ x: number; y: number; visible: boolean }>) => void;
  mountainResults: Array<{ peakY: number; baseRadius: number }>;
  maxHeat: number;
  beacons: StakeholderBeaconData[];
  onBeaconHover: (beacon: StakeholderBeaconData, sx: number, sy: number, pinned?: boolean) => void;
  onBeaconOut: () => void;
  isFocusMode: boolean;
  focusClusterIds: Set<string>;
  focusBeacons: Record<string, StakeholderBeaconData[]>;
  onFocusEnter: (id: string) => void;
  /** Arc hover: index into connections[] of the arc being hovered, or null */
  hoveredArcIdx: number | null;
  onArcHover: (idx: number, midpoint: THREE.Vector3) => void;
  onArcOut: () => void;
  /** Presentation Mode */
  isPresenting: boolean;
  presenterCamRef: React.MutableRefObject<PresenterCamState>;
  /** issue_id of the chapter currently being presented (null for intro/closing) */
  presentingIssueId: string | null;
  /** Beacons to show during the current presentation chapter */
  presentationBeacons: StakeholderBeaconData[];
  /** Index of the beacon whose tooltip is currently auto-displayed (null = none) */
  presentationBeaconIdx: number | null;
  onPresentationBeaconHover: (beacon: StakeholderBeaconData, sx: number, sy: number, pinned?: boolean) => void;
  onPresentationBeaconOut: () => void;
}

function Scene({
  peaks, sortedPeaks, connections, clientId, window_, scope,
  hoveredId, selectedId, onHover, onSelect,
  onSummitPositions, mountainResults, maxHeat, onResetReady, beacons, onBeaconHover, onBeaconOut, isFocusMode, focusClusterIds, focusBeacons, onFocusEnter,
  hoveredArcIdx, onArcHover, onArcOut,
  isPresenting, presenterCamRef, presentingIssueId,
  presentationBeacons, presentationBeaconIdx, onPresentationBeaconHover, onPresentationBeaconOut,
}: SceneProps & { onResetReady?: (fn: () => void) => void }) {
  const seed = useMemo(() => hash32(`${clientId}|${window_}|${scope}`), [clientId, window_, scope]);
  const { stamps, positions } = useMemo(
    () => peaksToStamps(peaks, seed, maxHeat),
    [peaks, seed, maxHeat]
  );
  const totalWidth = (peaks.length - 1) * 13;
  const peakYs = mountainResults.map(r => r.peakY);
  // Build RidgePeak descriptors for the continuous terrain mesh.
  // We attach the sentiment color so the ridge can tint its foothills.
  const ridgePeaks: (RidgePeak & { color: THREE.Color })[] = useMemo(
    () => stamps.map((stamp, i) => ({
      wx:         positions[i].x,
      wz:         positions[i].z,
      peakY:      mountainResults[i]?.peakY      ?? 6,
      baseRadius: mountainResults[i]?.baseRadius ?? 6,
      color:      stamp.color,
    })),
    [stamps, positions, mountainResults]
  );
  // Derive saddle edges from the connections array.
  // Only connections with a saddle_weight > 0 contribute to ridge formation.
  // The indexA/indexB refer to positions in the stamps/positions arrays (sorted by heat desc).
  const saddleEdges: SaddleEdge[] = useMemo(() => {
    const edges: SaddleEdge[] = [];
    for (const conn of connections) {
      const sw = (conn as TerrainConnection & { saddle_weight?: number }).saddle_weight ?? 0;
      if (sw <= 0) continue;
      // from_rank and to_rank are indices into the sorted peaks array
      const iA = conn.from_rank;
      const iB = conn.to_rank;
      if (iA < 0 || iB < 0 || iA >= stamps.length || iB >= stamps.length) continue;
      edges.push({ indexA: iA, indexB: iB, saddleWeight: sw });
    }
    return edges;
  }, [connections, stamps]);


  const target = useMemo(() => new THREE.Vector3(-75, 0, 0), []);
  const resetCameraRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const id = setInterval(() => {
      if (resetCameraRef.current) {
        onResetReady?.(resetCameraRef.current);
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, [onResetReady]);
  const defaultCam = useMemo(() => {
    const totalW = Math.max((peaks.length - 1) * 13, 60);
    const PAN_OFFSET = -75;
    const camX = PAN_OFFSET;
    const camY = Math.max(maxHeat * CAMERA_HEIGHT_MULTIPLIER, 30);
    const camZ = totalW * 0.46;
    return {
      position: new THREE.Vector3(camX, camY, camZ),
      target: new THREE.Vector3(PAN_OFFSET, 0, 0),
    };
  }, [peaks.length, maxHeat]);

  // Build a map from issue_id to sorted index for connection lookup
  const idToIdx = useMemo(() => {
    const m: Record<string, number> = {};
    sortedPeaks.forEach((p, i) => { m[p.issue_id] = i; });
    return m;
  }, [sortedPeaks]);
  // ── Focus Mode camera controller ─────────────────────────────────────────
  // Declared here (after all stamps/positions/mountainResults useMemos) to
  // avoid a Temporal Dead Zone in the minified bundle.
  const focusCamRef = useRef<FocusCamState>({ active: false, targetPos: new THREE.Vector3(), orbitAngle: 0, userInteracting: false, focusZoomRadius: 0 });
  // Sync focus camera target when isFocusMode or selectedId changes
  useEffect(() => {
    if (isFocusMode && selectedId) {
      const idx = stamps.findIndex(s => s.id === selectedId);
      if (idx >= 0 && idx < positions.length) {
        const pos = positions[idx];
        const peakY = mountainResults[idx]?.peakY ?? 8;
        focusCamRef.current.targetPos.set(pos.x, peakY * 0.3, pos.z);
        focusCamRef.current.active = true;
        focusCamRef.current.orbitAngle = 0;
      }
    } else {
      focusCamRef.current.active = false;
    }
  }, [isFocusMode, selectedId, stamps, positions, mountainResults]);

  return (
    <>
      {/* Focus Mode camera controller */}
      {isFocusMode && <FocusCameraController
        focusCamRef={focusCamRef}
        defaultTarget={target}
        isFocusMode={isFocusMode}
      />}
      {/* Presentation Mode camera controller */}
      <PresentationCameraController
        presenterCamRef={presenterCamRef}
        isPresenting={isPresenting}
      />
      {/* Lighting */}
      {/* Fix #2: Increase ambient light for subtle base fill (preserves drama) */}
      <ambientLight intensity={0.35} color={0x8899cc} />
      <directionalLight
        position={[60, 90, 50]}
        intensity={1.6}
        color={0xfff5e0}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-200}
        shadow-camera-right={200}
        shadow-camera-top={100}
        shadow-camera-bottom={-50}
        shadow-camera-far={400}
      />
      <hemisphereLight args={[0x1a2a4a, 0x080d18, 0.5]} />
      {/* Fix #2: Strengthen back light to illuminate dark side of peaks */}
      <directionalLight position={[-60, 35, -50]} intensity={0.60} color={0x4466aa} />

      {/* Sky backdrop */}
      <mesh position={[0, maxHeat * 0.4, -60]}>
        <planeGeometry args={[500, 120]} />
        <meshBasicMaterial color={0x0b1628} transparent opacity={0.98} side={THREE.DoubleSide} />
      </mesh>

      {/* Ridge terrain — continuous heightfield beneath the mountains */}
      <RidgeMesh ridgePeaks={ridgePeaks} seed={seed} saddleEdges={saddleEdges} />
      {/* Flat ground plane extends beyond the ridge for visual grounding */}
      <GroundPlane width={totalWidth} />

      {/* Mountains */}
      {stamps.map((stamp, i) => {
        // During presentation: highlight the active issue, fade all others.
        // During normal / focus mode: use the existing hover/select/focus logic.
        const isPresentingThis = isPresenting && presentingIssueId === stamp.id;
        const isHighlighted = isPresenting
          ? isPresentingThis
          : (hoveredId === stamp.id || selectedId === stamp.id);
        const isFaded = isPresenting
          ? (presentingIssueId !== null && !isPresentingThis)   // intro/closing: no fade; issue chapters: fade others
          : (isFocusMode && !focusClusterIds.has(stamp.id));
        return (
          <Mountain
            key={stamp.id}
            stamp={stamp}
            position={positions[i]}
            seed={seed}
            isHighlighted={isHighlighted}
            isPinned={isPresenting ? isPresentingThis : selectedId === stamp.id}
            isFaded={isFaded}
            onHover={onHover}
            onClick={onSelect}
            onDoubleClick={onFocusEnter}
          />
        );
      })}

      {/* Stakeholder beacons — pinned issue always; cluster issues in Focus Mode */}
      {selectedId !== null && (() => {
        const pinnedIdx = stamps.findIndex(s => s.id === selectedId);
        if (pinnedIdx < 0 || pinnedIdx >= positions.length) return null;
        const pinnedPos   = positions[pinnedIdx];
        const pinnedMR    = mountainResults[pinnedIdx];
        const baseRadius  = pinnedMR?.baseRadius ?? 6;
        const pinnedPeakY = pinnedMR?.peakY ?? 8;
        return (
          <>
            {/* Pinned issue — top 5 beacons */}
            <StakeholderBeacons
              beacons={beacons}
              mountainPos={pinnedPos}
              baseRadius={baseRadius}
              peakY={pinnedPeakY}
              onBeaconHover={onBeaconHover}
              onBeaconOut={onBeaconOut}
            />
            {/* Related cluster issues in Focus Mode — top 3 beacons each */}
            {isFocusMode && Array.from(focusClusterIds).filter(id => id !== selectedId).map(id => {
              const idx = stamps.findIndex(s => s.id === id);
              if (idx < 0 || idx >= positions.length) return null;
              const clusterBeacons = focusBeacons[id] ?? [];
              if (clusterBeacons.length === 0) return null;
              const mr = mountainResults[idx];
              return (
                <StakeholderBeacons
                  key={id}
                  beacons={clusterBeacons}
                  mountainPos={positions[idx]}
                  baseRadius={mr?.baseRadius ?? 6}
                  peakY={mr?.peakY ?? 8}
                  onBeaconHover={onBeaconHover}
                  onBeaconOut={onBeaconOut}
                />
              );
            })}
          </>
        );
      })()}
      {/* Presentation Mode beacons — shown around the active mountain during a chapter */}
      {isPresenting && presentationBeacons.length > 0 && presentingIssueId !== null && (() => {
        const issueIdx = stamps.findIndex(s => s.id === presentingIssueId);
        if (issueIdx < 0 || issueIdx >= positions.length) return null;
        const mPos = positions[issueIdx];
        const mr   = mountainResults[issueIdx];
        return (
          <PresentationBeacons
            beacons={presentationBeacons}
            activeBeaconIdx={presentationBeaconIdx}
            mountainPos={mPos}
            baseRadius={mr?.baseRadius ?? 6}
            peakY={mr?.peakY ?? 8}
            onBeaconHover={onPresentationBeaconHover}
            onBeaconOut={onPresentationBeaconOut}
          />
        );
      })()}

      {/* Spillover arcs — use idToIdx to find correct sorted positions */}
      {connections.map((conn, ci) => {
        const fi = idToIdx[conn.from_id];
        const ti = idToIdx[conn.to_id];
        if (fi === undefined || ti === undefined) return null;
        if (fi >= positions.length || ti >= positions.length) return null;
        const focusedId = hoveredId ?? selectedId;
        const isHighlighted = focusedId !== null &&
          (conn.from_id === focusedId || conn.to_id === focusedId);
        const hasFocus = focusedId !== null;
        // In Focus Mode, hide arcs where neither endpoint is in the focus cluster
        const isFocusHidden = isFocusMode && !(
          focusClusterIds.has(conn.from_id) && focusClusterIds.has(conn.to_id)
        );
        return (
          <ConnectionArc
            key={ci}
            fromPos={positions[fi]}
            toPos={positions[ti]}
            fromPeakY={peakYs[fi] ?? 6}
            toPeakY={peakYs[ti] ?? 6}
            weight={conn.weight ?? 0.5}
            isHighlighted={isHighlighted}
            hasFocus={hasFocus}
            isFocusHidden={isFocusHidden}
            isArcHovered={hoveredArcIdx === ci}
            onArcHover={(midpoint) => onArcHover(ci, midpoint)}
            onArcOut={onArcOut}
          />
        );
      })}

      {/* Summit tracker for DOM labels */}
      <SummitTracker
        positions={positions}
        peakYs={peakYs}
        onUpdate={onSummitPositions}
      />

      {/* Fog */}
      <fog attach="fog" args={[0x060c18, 120, 320]} />

      {/* Camera */}
      <CameraSetup peakCount={peaks.length} maxHeat={maxHeat} />
      <CameraControls target={target} resetRef={resetCameraRef} defaultCam={defaultCam} focusCamRef={focusCamRef} isFocusMode={isFocusMode} isPresenting={isPresenting} presenterCamRef={presenterCamRef} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DOM Label Layer
// Two modes:
//   - Default: compact rank badge (always visible, non-overlapping via opacity)
//   - Hover/Select: full card with title, HEAT, evidence, etc.
// ─────────────────────────────────────────────────────────────────────────────
interface LabelLayerProps {
  sortedPeaks: TerrainPeak[];
  screenPositions: Array<{ x: number; y: number; visible: boolean }>;
  hoveredId: string | null;
  selectedId: string | null;
  connections: TerrainConnection[];
}

function LabelLayer({ sortedPeaks, screenPositions, hoveredId, selectedId, connections }: LabelLayerProps) {
  // Build a lookup from issue_id → title+rank for the Connections list
  const peakById = useMemo(() => {
    const m: Record<string, TerrainPeak> = {};
    sortedPeaks.forEach(p => { m[p.issue_id] = p; });
    return m;
  }, [sortedPeaks]);
  return (
    <>
      {sortedPeaks.map((peak, i) => {
        const pos = screenPositions[i];
        if (!pos || !pos.visible) return null;

        const isActive = hoveredId === peak.issue_id || selectedId === peak.issue_id;
        const sColor = sentimentHex(peak.sentiment_state);

        if (isActive) {
          // Full detail card
          return (
            <div
              key={peak.issue_id}
              style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'none',
                zIndex: 30,
              }}
            >
              <div style={{
                background: 'rgba(4,8,18,0.96)',
                border: `2px solid ${sColor}`,
                borderRadius: 8,
                padding: '9px 13px',
                minWidth: 170,
                maxWidth: 220,
                color: '#fff',
                fontSize: 11,
                lineHeight: 1.55,
                boxShadow: `0 0 18px ${sColor}55, 0 4px 20px rgba(0,0,0,0.7)`,
              }}>
                <div style={{
                  fontWeight: 700, color: sColor, fontSize: 12,
                  marginBottom: 4, lineHeight: 1.35,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  #{peak.rank} {peak.title}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 10px' }}>
                  <div style={{ color: '#888' }}>HEAT</div>
                  <div style={{ fontWeight: 700, color: '#ffd700' }}>{peak.heat_now.toFixed(1)}</div>
                  {peak.heat_delta !== null && peak.heat_delta !== undefined && (
                    <>
                      <div style={{ color: '#888' }}>Δ HEAT</div>
                      <div style={{
                        fontWeight: 600,
                        color: peak.heat_delta > 0 ? '#ff6b6b' : '#6bff6b',
                      }}>
                        {peak.heat_delta > 0 ? '+' : ''}{peak.heat_delta.toFixed(1)}
                      </div>
                    </>
                  )}
                  <div style={{ color: '#888' }}>Evidence</div>
                  <div style={{ fontWeight: 600 }}>{peak.evidence_count_now}</div>
                  <div style={{ color: '#888' }}>Sentiment</div>
                  <div style={{ fontWeight: 600, color: sColor }}>{peak.sentiment_state}</div>
                </div>
                {peak.spillover_percentage != null && peak.spillover_percentage > 0 && (
                  <div style={{ color: '#ff9500', fontSize: 10, marginTop: 3 }}>
                    Spillover: {peak.spillover_percentage.toFixed(0)}%
                  </div>
                )}
                {peak.is_emerging && (
                  <div style={{ color: '#ffff00', fontWeight: 700, fontSize: 10, marginTop: 2 }}>
                    ⚡ Emerging
                  </div>
                )}
                {selectedId === peak.issue_id && (() => {
                  // Connections list — only for pinned (selected) peaks
                  const linked = connections
                    .filter(c => c.from_id === peak.issue_id || c.to_id === peak.issue_id)
                    .map(c => {
                      const otherId = c.from_id === peak.issue_id ? c.to_id : c.from_id;
                      const other = peakById[otherId];
                      return { other, shared: c.shared_stakeholders ?? 1, weight: c.weight ?? 0.5 };
                    })
                    .filter(x => x.other)
                    .sort((a, b) => b.shared - a.shared)
                    .slice(0, 5);
                  if (linked.length === 0) return null;
                  return (
                    <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 5 }}>
                      <div style={{ color: '#ff9500', fontSize: 10, fontWeight: 700, marginBottom: 3 }}>
                        Connections ({linked.length})
                      </div>
                      {linked.map(({ other, shared }) => (
                        <div key={other.issue_id} style={{
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 6, marginBottom: 2,
                        }}>
                          <div style={{
                            color: '#ccc', fontSize: 10,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 140,
                          }}>
                            #{other.rank} {other.title}
                          </div>
                          <div style={{
                            color: '#ff9500', fontSize: 10, fontWeight: 700,
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            {shared} shared
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div style={{ width: 1, height: 10, background: sColor, margin: '0 auto', opacity: 0.7 }} />
            </div>
          );
        }

        // Compact rank badge — only show for top 20 to avoid clutter
        if (peak.rank > 20) return null;

        return (
          <div
            key={peak.issue_id}
            style={{
              position: 'absolute',
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              zIndex: 15,
            }}
          >
            <div style={{
              background: 'rgba(4,8,18,0.82)',
              border: `1px solid ${sColor}`,
              borderRadius: 4,
              padding: '2px 6px',
              color: sColor,
              fontSize: 10,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              boxShadow: `0 0 6px ${sColor}33`,
            }}>
              #{peak.rank} · {peak.heat_now.toFixed(0)}
            </div>
            <div style={{ width: 1, height: 8, background: sColor, margin: '0 auto', opacity: 0.5 }} />
          </div>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
interface IssueTerrain3DProps {
  clientId: string;
  window?: string;
  scope?: string;
}

const IssueTerrain3D: React.FC<IssueTerrain3DProps> = ({
  clientId,
  window: windowProp = '30d',
  scope = 'combined',
}) => {
  const [terrainData, setTerrainData] = useState<TerrainData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [summitPositions, setSummitPositions] = useState<Array<{ x: number; y: number; visible: boolean }>>([]);
  const [beaconData, setBeaconData] = useState<StakeholderBeaconData[]>([]);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [focusClusterIds, setFocusClusterIds] = useState<Set<string>>(new Set());
  const [focusBeacons, setFocusBeacons] = useState<Record<string, StakeholderBeaconData[]>>({});
  const [hoveredBeacon, setHoveredBeacon] = useState<StakeholderBeaconData | null>(null);
  const [beaconTooltipPos, setBeaconTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const beaconPinnedRef = useRef(false);
  const handleBeaconHover = useCallback((beacon: StakeholderBeaconData, sx: number, sy: number, pinned?: boolean) => {
    setHoveredBeacon(beacon);
    setBeaconTooltipPos({ x: sx, y: sy });
    if (pinned) beaconPinnedRef.current = true;
  }, []);
  const handleBeaconOut = useCallback(() => {
    // Only dismiss on pointer-out if the tooltip is not pinned by a tap
    if (!beaconPinnedRef.current) setHoveredBeacon(null);
  }, []);
  const handleBeaconDismiss = useCallback(() => {
    beaconPinnedRef.current = false;
    setHoveredBeacon(null);
  }, []);

  // Arc hover tooltip state
  const [hoveredArcIdx, setHoveredArcIdx] = useState<number | null>(null);
  const [arcTooltipScreen, setArcTooltipScreen] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const arcTooltipCameraRef = useRef<THREE.Camera | null>(null);
  const arcTooltipSizeRef = useRef<{ width: number; height: number }>({ width: 800, height: 600 });
  const handleArcHover = useCallback((idx: number, midpoint: THREE.Vector3) => {
    setHoveredArcIdx(idx);
    // Project 3D midpoint to screen coords using the canvas size
    const cam = arcTooltipCameraRef.current;
    const sz = arcTooltipSizeRef.current;
    if (cam) {
      const v = midpoint.clone().project(cam);
      setArcTooltipScreen({
        x: (v.x * 0.5 + 0.5) * sz.width,
        y: (-v.y * 0.5 + 0.5) * sz.height,
      });
    }
  }, []);
  const handleArcOut = useCallback(() => setHoveredArcIdx(null), []);

  // Terrain data derived values (must be before presentation state for goToChapter closure)
  const peaks = terrainData?.peaks ?? [];
  const connections = terrainData?.connections ?? [];
  const summary = terrainData?.summary;
  const maxHeat = summary?.max_heat ?? 70;

  // Sort peaks high→low HEAT (same order as geometry placement)
  const sortedPeaks = useMemo(
    () => [...peaks].sort((a, b) => b.heat_now - a.heat_now),
    [peaks]
  );

  // ── Presentation Mode state ─────────────────────────────────────────────────────────────────
  const [presentationPhase, setPresentationPhase] = useState<PresentationPhase>('idle');
  const [presentationData, setPresentationData] = useState<PresentationResponse | null>(null);
  const [presentationError, setPresentationError] = useState<string | null>(null);
  // chapterIndex: 0 = intro, 1..N = chapters, N+1 = closing
  const [chapterIndex, setChapterIndex] = useState(0);
  // issue_id of the chapter currently being presented (null for intro/closing)
  const [presentingIssueId, setPresentingIssueId] = useState<string | null>(null);
  // Beacon sequencer state
  const [presentationBeacons, setPresentationBeacons] = useState<StakeholderBeaconData[]>([]);
  const [presentationBeaconIdx, setPresentationBeaconIdx] = useState<number | null>(null);
  const beaconSeqTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const presentationBeaconsRef = useRef<Record<string, StakeholderBeaconData[]>>({});
  // Audio elements keyed by chapter index
  const audioRefs = useRef<Record<number, HTMLAudioElement>>({});
  const audioUrlsRef = useRef<Record<number, string>>({});
  // Presenter camera ref (mutable state for the 3D controller)
  const presenterCamRef = useRef<PresenterCamState>({
    active: false,
    targetPos: new THREE.Vector3(),
    orbitAngle: 0,
    orbitRadius: PRESENTATION_INITIAL_ORBIT_RADIUS,
    userInteracting: false,
    resumeAt: 0,
  });
  const isPresenting = presentationPhase === 'playing' || presentationPhase === 'paused';

  // Total chapters = 1 (intro) + N (issue chapters) + 1 (closing)
  const totalChapters = presentationData
    ? 2 + presentationData.script.chapters.length
    : 0;

  /** Build the text for a given chapter index */
  const getChapterText = useCallback((idx: number): string => {
    if (!presentationData) return '';
    const { script } = presentationData;
    if (idx === 0) return script.intro.text;
    if (idx === totalChapters - 1) return script.closing.text;
    const ch = script.chapters[idx - 1];
    if (!ch) return '';
    return `${ch.summary} ${ch.why_it_matters} ${ch.watch_for}`;
  }, [presentationData, totalChapters]);

  /** Build the headline for a given chapter index */
  const getChapterHeadline = useCallback((idx: number): string => {
    if (!presentationData) return '';
    const { script } = presentationData;
    if (idx === 0) return script.intro.headline;
    if (idx === totalChapters - 1) return script.closing.headline;
    return script.chapters[idx - 1]?.headline ?? '';
  }, [presentationData, totalChapters]);

  /** Fetch TTS audio for a single chapter index and store the URL */
  const fetchChapterAudio = useCallback(async (idx: number, text: string): Promise<void> => {
    try {
      const res = await fetch('/api/tts/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'onyx', speed: 1.25, model: 'tts-1' }),
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.url) {
        audioUrlsRef.current[idx] = json.url;
      }
    } catch { /* non-fatal */ }
  }, []);

  /** Pre-fetch TTS audio for all chapters (background, non-blocking) */
  const prefetchAudio = useCallback(async (data: PresentationResponse) => {
    const { script } = data;
    const chapters = [
      { idx: 0, text: script.intro.text },
      ...script.chapters.map((ch, i) => ({
        idx: i + 1,
        text: `${ch.summary} ${ch.why_it_matters} ${ch.watch_for}`,
      })),
      { idx: script.chapters.length + 1, text: script.closing.text },
    ];
    await Promise.all(chapters.map(({ idx, text }) => fetchChapterAudio(idx, text)));
  }, [fetchChapterAudio]);

  /** Start Presentation Mode: fetch script, await intro audio, then start playing */
  const handleStartPresentation = useCallback(async () => {
    setPresentationPhase('loading');
    setPresentationError(null);
    try {
      const res = await fetch(`/api/issue-terrain/${clientId}/presentation`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: PresentationResponse = await res.json();
      setPresentationData(data);
      // Await the intro audio specifically so it is ready the moment we start playing.
      // All remaining chapters are fetched in the background concurrently.
      await fetchChapterAudio(0, data.script.intro.text);
      // Background-fetch remaining chapters (non-blocking from here)
      const remainingChapters = [
        ...data.script.chapters.map((ch, i) => ({
          idx: i + 1,
          text: `${ch.summary} ${ch.why_it_matters} ${ch.watch_for}`,
        })),
        { idx: data.script.chapters.length + 1, text: data.script.closing.text },
      ];
      Promise.all(remainingChapters.map(({ idx, text }) => fetchChapterAudio(idx, text))).catch(() => {});
      // Pre-fetch stakeholder beacons for all presentation issue chapters (background, non-blocking)
      Promise.all(
        data.issues.map(issue =>
          fetch(`/api/issue-terrain/issue/${issue.issue_id}/stakeholders?client_id=${clientId}&limit=3`)
            .then(r => r.ok ? r.json() : Promise.resolve({ stakeholders: [] }))
            .then(d => ({ id: issue.issue_id, beacons: (d.stakeholders ?? []) as StakeholderBeaconData[] }))
            .catch(() => ({ id: issue.issue_id, beacons: [] as StakeholderBeaconData[] }))
        )
      ).then(results => {
        const map: Record<string, StakeholderBeaconData[]> = {};
        results.forEach(r => { map[r.id] = r.beacons; });
        presentationBeaconsRef.current = map;
      }).catch(() => {});
      // Set up the intro camera: wide overview centred on the full terrain.
      const n = sortedPeaks.length;
      const terrainHalfWidth = ((n - 1) * 13) / 2;
      const overviewRadius = Math.max(80, terrainHalfWidth * 1.1);
      presenterCamRef.current.active = true;
      presenterCamRef.current.orbitAngle = 0;
      presenterCamRef.current.orbitRadius = overviewRadius;
      presenterCamRef.current.targetPos.set(-terrainHalfWidth, 0, 0);
      setPresentingIssueId(null);
      setChapterIndex(0);
      setPresentationPhase('playing');
    } catch (err: any) {
      setPresentationError(err.message ?? 'Failed to load presentation');
      setPresentationPhase('idle');
    }
  }, [clientId, fetchChapterAudio, sortedPeaks]);

  /** Cancel any running beacon sequence timers */
  const clearBeaconSeq = useCallback(() => {
    beaconSeqTimersRef.current.forEach(t => clearTimeout(t));
    beaconSeqTimersRef.current = [];
    setPresentationBeacons([]);
    setPresentationBeaconIdx(null);
  }, []);

  /** Exit Presentation Mode */
  const handleExitPresentation = useCallback(() => {
    // Stop all audio
    Object.values(audioRefs.current).forEach(a => { a.pause(); a.currentTime = 0; });
    audioRefs.current = {};
    audioUrlsRef.current = {};
    presenterCamRef.current.active = false;
    setPresentationPhase('idle');
    setPresentationData(null);
    setChapterIndex(0);
    setPresentingIssueId(null);
    clearBeaconSeq();
    presentationBeaconsRef.current = {};
  }, [clearBeaconSeq]);

  /** Play audio for the current chapter */
  const playChapterAudio = useCallback((idx: number) => {
    const url = audioUrlsRef.current[idx];
    if (!url) return;
    // Stop any currently playing audio
    Object.values(audioRefs.current).forEach(a => { a.pause(); a.currentTime = 0; });
    const audio = new Audio(url);
    audioRefs.current[idx] = audio;
    audio.play().catch(() => {});
    // Auto-advance to next chapter when audio ends
    audio.onended = () => {
      setChapterIndex(prev => {
        const next = prev + 1;
        if (next < totalChapters) {
          return next;
        } else {
          // Presentation complete
          presenterCamRef.current.active = false;
          setPresentationPhase('idle');
          return prev;
        }
      });
    };
  }, [totalChapters]);

  /** Navigate to a specific chapter */
  const goToChapter = useCallback((idx: number, data?: PresentationResponse) => {
    const pData = data ?? presentationData;
    if (!pData) return;
    setChapterIndex(idx);
    // Update camera target for this chapter
    const { script } = pData;
    if (idx === 0 || idx === totalChapters - 1) {
      // Intro / closing: wide overview centred on the terrain.
      // Terrain spans from x=0 (highest peak) to x=(N-1)*13 (lowest peak).
      // The camera target should sit at the midpoint of that range.
      const n = sortedPeaks.length;
      const terrainHalfWidth = ((n - 1) * 13) / 2;
      // Orbit radius scales with terrain width so all mountains are visible.
      const overviewRadius = Math.max(80, terrainHalfWidth * 1.1);
      presenterCamRef.current.targetPos.set(-terrainHalfWidth, 0, 0);
      presenterCamRef.current.orbitRadius = overviewRadius;
      // No specific issue is highlighted during intro/closing
      setPresentingIssueId(null);
      // Clear any running beacon sequence
      beaconSeqTimersRef.current.forEach(t => clearTimeout(t));
      beaconSeqTimersRef.current = [];
      setPresentationBeacons([]);
      setPresentationBeaconIdx(null);
    } else {
      // Chapter: fly to the issue's mountain
      const ch = script.chapters[idx - 1];
      if (ch) {
        // Set the presenting issue so the mountain gets highlighted
        setPresentingIssueId(ch.issue_id);
        const issueIdx = sortedPeaks.findIndex(p => p.issue_id === ch.issue_id);
        const pos = presenterPositionsRef.current[issueIdx];
        if (pos) {
          const mr = presenterMountainResultsRef.current[issueIdx];
          const peakY = mr?.peakY ?? 20;
          // Fix #1: Raise target Y position for better framing of tall peaks
          presenterCamRef.current.targetPos.set(pos.x, peakY * 0.4, pos.z);
          // Fix #1: Dynamic orbit radius ensures full peak visibility for all HEAT scores
          const dynamicRadius = Math.max(45, peakY * 0.75);
          presenterCamRef.current.orbitRadius = dynamicRadius;
        }
        // Cancel previous beacon sequence and start a new one for this chapter
        beaconSeqTimersRef.current.forEach(t => clearTimeout(t));
        beaconSeqTimersRef.current = [];
        setPresentationBeaconIdx(null);
        const issueBeacons = presentationBeaconsRef.current[ch.issue_id] ?? [];
        if (issueBeacons.length > 0) {
          // Show beacons immediately as 3D pillars
          setPresentationBeacons(issueBeacons);
          // Schedule tooltip reveals: 1.5s, 5.5s, 9.5s; dismiss all at 13.5s
          const DWELL = 4000; // ms per beacon
          const START = 1500; // ms before first tooltip
          issueBeacons.forEach((_, bi) => {
            beaconSeqTimersRef.current.push(
              setTimeout(() => setPresentationBeaconIdx(bi), START + bi * DWELL)
            );
          });
          beaconSeqTimersRef.current.push(
            setTimeout(() => setPresentationBeaconIdx(null), START + issueBeacons.length * DWELL)
          );
        } else {
          setPresentationBeacons([]);
        }
      }
    }
    presenterCamRef.current.orbitAngle = 0;
    // Play audio for this chapter
    if (presentationPhase === 'playing') playChapterAudio(idx);
  }, [presentationData, totalChapters, sortedPeaks, presentationPhase, playChapterAudio]);

  // Refs for passing terrain positions to the goToChapter logic
  const presenterPositionsRef = useRef<THREE.Vector3[]>([]);
  const presenterMountainResultsRef = useRef<Array<{ peakY: number; baseRadius: number }>>([]);

  // When chapterIndex changes (e.g. auto-advance from audio end), update camera + highlight
  useEffect(() => {
    if (!isPresenting || !presentationData) return;
    goToChapter(chapterIndex);
  // goToChapter is stable (useCallback); chapterIndex drives the transition.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterIndex, goToChapter]);

  // When the presentation first transitions to 'playing' (from 'loading'),
  // kick off the intro audio immediately — the chapterIndex useEffect above
  // won't fire because chapterIndex is still 0 (unchanged).
  const prevPhaseRef = useRef<PresentationPhase>('idle');
  useEffect(() => {
    if (presentationPhase === 'playing' && prevPhaseRef.current === 'loading') {
      playChapterAudio(0);
    }
    prevPhaseRef.current = presentationPhase;
  }, [presentationPhase, playChapterAudio]);

  // Play/Pause toggle
  const handleTogglePlayPause = useCallback(() => {
    if (presentationPhase === 'playing') {
      // Pause audio
      const audio = audioRefs.current[chapterIndex];
      if (audio) audio.pause();
      setPresentationPhase('paused');
    } else if (presentationPhase === 'paused') {
      // Resume audio
      const audio = audioRefs.current[chapterIndex];
      if (audio) audio.play().catch(() => {});
      else playChapterAudio(chapterIndex);
      setPresentationPhase('playing');
    }
  }, [presentationPhase, chapterIndex, playChapterAudio]);

  const seed = useMemo(() => hash32(`${clientId}|${windowProp}|${scope}`), [clientId, windowProp, scope]);
  const handleFocusEnter = useCallback((id: string) => {
    setSelectedId(id);
    setIsFocusMode(true);
    // Build focus cluster: issues connected with shared_stakeholders > 2, top 6
    const cluster = connections
      .filter(c => {
        const shared = c.shared_stakeholders ?? 0;
        return shared > 2 && (c.from_id === id || c.to_id === id);
      })
      .map(c => ({
        id: c.from_id === id ? c.to_id : c.from_id,
        shared: c.shared_stakeholders ?? 0,
      }))
      .sort((a, b) => b.shared - a.shared)
      .slice(0, 6)
      .map(x => x.id);
    const clusterSet = new Set([id, ...cluster]);
    setFocusClusterIds(clusterSet);
    // Fetch beacons for each related cluster issue (top 3 each)
    const relatedIds = cluster;
    Promise.all(
      relatedIds.map(relId =>
        fetch(`/api/issue-terrain/issue/${relId}/stakeholders?client_id=${clientId}&limit=3`)
          .then(r => r.ok ? r.json() : Promise.resolve({ stakeholders: [] }))
          .then(d => ({ id: relId, beacons: d.stakeholders ?? [] as StakeholderBeaconData[] }))
          .catch(() => ({ id: relId, beacons: [] as StakeholderBeaconData[] }))
      )
    ).then(results => {
      const map: Record<string, StakeholderBeaconData[]> = {};
      results.forEach(r => { map[r.id] = r.beacons; });
      setFocusBeacons(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, clientId]);

  const handleFocusExit = useCallback(() => {
    setIsFocusMode(false);
    setFocusClusterIds(new Set());
    setFocusBeacons({});
  }, []);
  const resetCameraFnRef = useRef<(() => void) | null>(null);
  const handleResetReady = useCallback((fn: () => void) => {
    resetCameraFnRef.current = fn;
  }, []);
  const handleResetCamera = useCallback(() => {
    resetCameraFnRef.current?.();
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ window: windowProp, scope });
    fetch(`/api/issue-terrain/${clientId}?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: TerrainData) => {
        setTerrainData(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [clientId, windowProp, scope]);

  // Fetch stakeholder beacons when an issue is pinned
  useEffect(() => {
    if (!selectedId || !clientId) {
      setBeaconData([]);
      return;
    }
    fetch(`/api/issue-terrain/issue/${selectedId}/stakeholders?client_id=${clientId}&limit=5`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setBeaconData(data.stakeholders ?? []))
      .catch(() => setBeaconData([]));
  }, [selectedId, clientId]);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);


  // Build stamps using sorted order + exaggerated heights
  const { stamps } = useMemo(
    () => peaksToStamps(peaks, seed, maxHeat),
    [peaks, seed, maxHeat]
  );

  // Pre-compute geometry results for summit heights
  const mountainResults = useMemo(
    () => stamps.map(s => buildMountainGeometry(s, seed)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamps.map(s => `${s.id}:${s.heatNow}`).join(','), seed]
  );

  const handleSelect = useCallback((id: string) => {
    setSelectedId(prev => prev === id ? null : id);
  }, []);

  // Keep presenter position refs in sync with terrain geometry
  const { positions: presenterPositions } = useMemo(
    () => peaksToStamps(peaks, seed, maxHeat),
    [peaks, seed, maxHeat]
  );
  useEffect(() => {
    presenterPositionsRef.current = presenterPositions;
    presenterMountainResultsRef.current = mountainResults;
  }, [presenterPositions, mountainResults]);

  if (loading) {
    return (
      <div style={{
        width: '100%', height: '100dvh', minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(180deg, #060c18 0%, #0d1a2e 100%)',
        color: '#4488cc', fontSize: 16,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⛰</div>
          <div>Loading Issue Terrain…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%', height: '100dvh', minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#060c18', color: '#ff4444', fontSize: 14,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⚠</div>
          <div>Failed to load terrain data</div>
          <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (peaks.length === 0) {
    return (
      <div style={{
        width: '100%', height: '100dvh', minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#060c18', color: '#888', fontSize: 14,
      }}>
        No issues found for this client.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh', minHeight: '100vh',
        background: 'linear-gradient(180deg, #060c18 0%, #0d1a2e 60%, #111a30 100%)',
        overflow: 'hidden',
      }}
    >
      <Canvas
        shadows={{ type: THREE.PCFShadowMap }}
        camera={{ fov: 52, near: 0.5, far: 450 }}
        gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
        style={{ width: '100%', height: '100%' }}
        onPointerMissed={() => { setSelectedId(null); handleBeaconDismiss(); }}
      >
        <ArcTooltipCameraCapture
          cameraRef={arcTooltipCameraRef}
          sizeRef={arcTooltipSizeRef}
        />
        <Scene
          peaks={peaks}
          sortedPeaks={sortedPeaks}
          connections={connections}
          clientId={clientId}
          window_={windowProp}
          scope={scope}
          hoveredId={hoveredId}
          selectedId={selectedId}
          onHover={setHoveredId}
          onSelect={handleSelect}
          onSummitPositions={setSummitPositions}
          mountainResults={mountainResults}
          maxHeat={maxHeat}
          beacons={beaconData}
          onBeaconHover={handleBeaconHover}
          onBeaconOut={handleBeaconOut}
          isFocusMode={isFocusMode}
          focusClusterIds={focusClusterIds}
          focusBeacons={focusBeacons}
          onFocusEnter={handleFocusEnter}
          onResetReady={handleResetReady}
          hoveredArcIdx={hoveredArcIdx}
          onArcHover={handleArcHover}
          onArcOut={handleArcOut}
          isPresenting={isPresenting}
          presenterCamRef={presenterCamRef}
          presentingIssueId={presentingIssueId}
          presentationBeacons={presentationBeacons}
          presentationBeaconIdx={presentationBeaconIdx}
          onPresentationBeaconHover={handleBeaconHover}
          onPresentationBeaconOut={handleBeaconOut}
        />
      </Canvas>

      {/* DOM labels */}
      <LabelLayer
        sortedPeaks={sortedPeaks}
        screenPositions={summitPositions}
        hoveredId={hoveredId}
        selectedId={selectedId}
        connections={connections}
      />

      {/* Beacon tooltip — secondary hover layer, zIndex 35 (above main tooltip 30) */}
      {hoveredBeacon && (() => {
        // During a presentation auto-sequence the tooltip is non-dismissable
        const isPresentationSeq = isPresenting && presentationBeaconIdx !== null;
        return (
        <div
          onClick={(!isPresentationSeq && beaconPinnedRef.current) ? handleBeaconDismiss : undefined}
          style={{
          position: 'absolute',
          left: beaconTooltipPos.x,
          top: beaconTooltipPos.y,
          transform: 'translate(-50%, calc(-100% - 10px))',
          pointerEvents: (!isPresentationSeq && beaconPinnedRef.current) ? 'auto' : 'none',
          cursor: (!isPresentationSeq && beaconPinnedRef.current) ? 'pointer' : 'default',
          zIndex: 35,
          background: 'rgba(4,8,18,0.92)',
          border: `1px solid ${hoveredBeacon.colour}`,
          borderRadius: 6,
          padding: '7px 10px',
          minWidth: 180,
          maxWidth: 220,
          boxShadow: `0 0 12px ${hoveredBeacon.colour}44`,
        }}>
          {/* Header: avatar + name/org */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            {/* Circular avatar with role-colour ring */}
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${hoveredBeacon.colour}`,
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {hoveredBeacon.profile_image ? (
                <img
                  src={hoveredBeacon.profile_image}
                  alt={hoveredBeacon.name}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ color: hoveredBeacon.colour, fontSize: 13, fontWeight: 700 }}>
                  {hoveredBeacon.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            {/* Name + org stacked */}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: 1.3 }}>
                {hoveredBeacon.name}
              </div>
              {hoveredBeacon.organization && (
                <div style={{
                  color: '#7799cc', fontSize: 10, marginTop: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {hoveredBeacon.organization}
                </div>
              )}
            </div>
          </div>
          {/* Coloured role indicator bar */}
          <div style={{ height: 1, background: hoveredBeacon.colour, borderRadius: 1, marginBottom: 6, opacity: 0.5 }} />
          {/* Stats grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '2px 8px', fontSize: 10,
          }}>
            <div style={{ color: '#888' }}>Role</div>
            <div style={{ color: hoveredBeacon.colour, fontWeight: 700 }}>{hoveredBeacon.role}</div>
            <div style={{ color: '#888' }}>Influence</div>
            <div style={{ fontWeight: 600, color: '#fff' }}>{hoveredBeacon.influence_level} / 5</div>
            <div style={{ color: '#888' }}>Interest</div>
            <div style={{ fontWeight: 600, color: '#fff' }}>{hoveredBeacon.interest_level} / 5</div>
          </div>
          {/* Last updated row */}
          {hoveredBeacon.last_updated && (() => {
            const d = new Date(hoveredBeacon.last_updated);
            const now = Date.now();
            const diffMs = now - d.getTime();
            const diffDays = Math.floor(diffMs / 86400000);
            const relTime = diffDays === 0 ? 'Today'
              : diffDays === 1 ? '1 day ago'
              : diffDays < 30 ? `${diffDays} days ago`
              : diffDays < 365 ? `${Math.floor(diffDays / 30)}mo ago`
              : `${Math.floor(diffDays / 365)}y ago`;
            return (
              <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ color: '#556', fontSize: 9 }}>
                  Updated <span style={{ color: '#88aacc' }}>{relTime}</span>
                  {hoveredBeacon.last_change_reason && (
                    <span style={{ color: '#556' }}> · {hoveredBeacon.last_change_reason}</span>
                  )}
                </div>
              </div>
            );
          })()}
          {/* Connector line */}
          <div style={{ width: 1, height: 10, background: hoveredBeacon.colour, margin: '4px auto 0', opacity: 0.7 }} />
          {/* Tap-to-dismiss hint — only shown when pinned by a touch tap (not during presentation) */}
          {!isPresentationSeq && beaconPinnedRef.current && (
            <div style={{ color: '#556', fontSize: 9, textAlign: 'center', marginTop: 4 }}>tap to dismiss</div>
          )}
          {/* Presentation sequence indicator */}
          {isPresentationSeq && presentationBeacons.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 6 }}>
              {presentationBeacons.map((_, di) => (
                <div key={di} style={{
                  width: di === presentationBeaconIdx ? 14 : 5,
                  height: 3,
                  borderRadius: 2,
                  background: di === presentationBeaconIdx ? hoveredBeacon.colour : 'rgba(255,255,255,0.2)',
                  transition: 'width 0.3s ease',
                }} />
              ))}
            </div>
          )}
        </div>
        );
      })()}
      {/* Arc tooltip — appears at arc midpoint when hovering a spillover connection */}
      {hoveredArcIdx !== null && (() => {
        const conn = connections[hoveredArcIdx];
        if (!conn) return null;
        const fromPeak = sortedPeaks.find(p => p.issue_id === conn.from_id);
        const toPeak   = sortedPeaks.find(p => p.issue_id === conn.to_id);
        if (!fromPeak || !toPeak) return null;
        const shared = conn.shared_stakeholders ?? 0;
        return (
          <div style={{
            position: 'absolute',
            left: arcTooltipScreen.x,
            top: arcTooltipScreen.y,
            transform: 'translate(-50%, calc(-100% - 14px))',
            pointerEvents: 'none',
            zIndex: 32,
            background: 'rgba(4,8,18,0.93)',
            border: '1px solid rgba(255,180,0,0.55)',
            borderRadius: 6,
            padding: '7px 11px',
            minWidth: 180,
            maxWidth: 260,
            boxShadow: '0 0 14px rgba(255,180,0,0.25)',
          }}>
            {/* Amber top bar */}
            <div style={{ height: 2, background: '#ffb400', borderRadius: 2, marginBottom: 6 }} />
            {/* Label */}
            <div style={{ color: '#ffb400', fontSize: 9, fontWeight: 700, letterSpacing: 0.8, marginBottom: 5, textTransform: 'uppercase' }}>
              ↔ Spillover Connection
            </div>
            {/* From issue */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 3 }}>
              <span style={{ color: '#888', fontSize: 9, minWidth: 28 }}>#{fromPeak.rank}</span>
              <span style={{ color: '#fff', fontSize: 10, fontWeight: 600, lineHeight: 1.3 }}>{fromPeak.title}</span>
            </div>
            {/* To issue */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 6 }}>
              <span style={{ color: '#888', fontSize: 9, minWidth: 28 }}>#{toPeak.rank}</span>
              <span style={{ color: '#fff', fontSize: 10, fontWeight: 600, lineHeight: 1.3 }}>{toPeak.title}</span>
            </div>
            {/* Shared stakeholders */}
            <div style={{
              borderTop: '1px solid rgba(255,180,0,0.2)', paddingTop: 5,
              color: '#aaa', fontSize: 10,
            }}>
              <span style={{ color: '#ffb400', fontWeight: 700 }}>{shared}</span>
              {' '}shared stakeholder{shared !== 1 ? 's' : ''}
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div style={{
        position: 'absolute', top: 12, left: 12,
        color: '#fff', zIndex: 20, pointerEvents: 'none',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>Issue Terrain</div>
        {summary && (
          <div style={{ fontSize: 11, color: '#7799cc', marginTop: 2 }}>
            {summary.peaks_returned} issues · avg HEAT {summary.avg_heat?.toFixed(1)} · max {summary.max_heat?.toFixed(1)}
          </div>
        )}
      </div>

      {/* Presentation Mode launch button (idle state) */}
      {!isPresenting && presentationPhase !== 'loading' && (
        <button
          onClick={handleStartPresentation}
          title="Start AI-narrated executive briefing"
          style={{
            position: 'absolute', top: 12, left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(4,8,18,0.88)',
            border: '1px solid rgba(100,160,255,0.45)',
            color: '#88bbff', padding: '6px 16px', borderRadius: 6,
            cursor: 'pointer', fontSize: 11, fontWeight: 700,
            zIndex: 25, letterSpacing: 0.4,
            display: 'flex', alignItems: 'center', gap: 7,
          }}
        >
          <span style={{ fontSize: 14 }}>▶</span> Present
        </button>
      )}

      {/* Presentation loading spinner */}
      {presentationPhase === 'loading' && (
        <div style={{
          position: 'absolute', top: 12, left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(4,8,18,0.88)',
          border: '1px solid rgba(100,160,255,0.3)',
          color: '#88bbff', padding: '6px 16px', borderRadius: 6,
          fontSize: 11, zIndex: 25, letterSpacing: 0.4,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>↻</span>
          Generating briefing…
        </div>
      )}

      {/* Presentation error */}
      {presentationError && (
        <div style={{
          position: 'absolute', top: 12, left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(18,4,4,0.92)',
          border: '1px solid rgba(255,80,80,0.5)',
          color: '#ff8888', padding: '6px 14px', borderRadius: 6,
          fontSize: 11, zIndex: 25,
        }}>
          ⚠ {presentationError}
        </div>
      )}

      {/* Controls hint */}
      <div style={{
        position: 'absolute', top: 12, right: 12,
        background: 'rgba(0,0,0,0.55)', color: '#aaa',
        padding: '6px 10px', borderRadius: 6, fontSize: 10,
        zIndex: 20, lineHeight: 1.8, pointerEvents: 'none',
      }}>
        <div>Drag to orbit · Scroll to zoom</div>
        <div>Right-drag to pan · Click to inspect</div>
        <div>Double-click to enter Focus Mode</div>
        <div>⌮ Reset View restores overview</div>
      </div>

      {/* Focus Mode indicator + Exit button */}
      {isFocusMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'rgba(4,8,18,0.88)',
          border: '1px solid rgba(255,180,0,0.5)',
          borderRadius: 8, padding: '6px 14px',
          zIndex: 25, pointerEvents: 'auto',
        }}>
          <span style={{ color: '#ffb400', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
            ◎ FOCUS MODE
          </span>
          <span style={{ color: '#666', fontSize: 11 }}>
            {focusClusterIds.size - 1} related issue{focusClusterIds.size !== 2 ? 's' : ''} visible
          </span>
          <button
            onClick={handleFocusExit}
            style={{
              background: 'rgba(255,180,0,0.12)', border: '1px solid rgba(255,180,0,0.4)',
              color: '#ffb400', padding: '3px 9px', borderRadius: 4,
              cursor: 'pointer', fontSize: 11, fontWeight: 700,
            }}
          >
            ✕ Exit Focus
          </button>
        </div>
      )}
      {/* Reset View */}
      <button
        onClick={handleResetCamera}
        title="Reset camera to default overview"
        style={{
          position: 'absolute', bottom: 46, right: 12,
          background: 'rgba(0,0,0,0.55)', border: '1px solid #444',
          color: '#ccc', padding: '5px 10px', borderRadius: 5,
          cursor: 'pointer', fontSize: 11, zIndex: 20,
        }}
      >
        ⌮ Reset View
      </button>
      {/* Fullscreen */}
      <button
        onClick={handleFullscreen}
        style={{
          position: 'absolute', bottom: 12, right: 12,
          background: 'rgba(0,0,0,0.55)', border: '1px solid #333',
          color: '#aaa', padding: '5px 10px', borderRadius: 5,
          cursor: 'pointer', fontSize: 11, zIndex: 20,
        }}
      >
        {isFullscreen ? '⊡ Exit' : '⊞ Fullscreen'}
      </button>

      {/* Legend */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12,
        background: 'rgba(0,0,0,0.65)', color: '#fff',
        padding: '8px 12px', borderRadius: 6, fontSize: 11,
        zIndex: 20, lineHeight: 1.9, pointerEvents: 'none',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 2, color: '#ccc' }}>Sentiment</div>
        <div><span style={{ color: '#ff4444' }}>●</span> Negative</div>
        <div><span style={{ color: '#00e5ff' }}>●</span> Positive</div>
        <div><span style={{ color: '#9090a0' }}>●</span> Neutral</div>
        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}>
          <span style={{ color: '#ff9500' }}>—</span> Spillover link
        </div>
        <div style={{ marginTop: 4, color: '#666', fontSize: 10 }}>
          Height = HEAT · Width = Evidence
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────────────────────── */}
      {/* Presentation Mode Player Overlay */}
      {/* ───────────────────────────────────────────────────────────────────────────── */}
      {isPresenting && presentationData && (() => {
        const headline = getChapterHeadline(chapterIndex);
        const text     = getChapterText(chapterIndex);
        const isIntro  = chapterIndex === 0;
        const isClosing = chapterIndex === totalChapters - 1;
        const chapterLabel = isIntro ? 'Introduction'
          : isClosing ? 'Closing'
          : `Issue ${chapterIndex} of ${totalChapters - 2}`;
        const audioReady = !!audioUrlsRef.current[chapterIndex];

        return (
          <>
            {/* Dark vignette overlay at bottom to frame the caption bar */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 220,
              background: 'linear-gradient(to top, rgba(2,5,12,0.92) 0%, rgba(2,5,12,0.55) 60%, transparent 100%)',
              pointerEvents: 'none', zIndex: 38,
            }} />

            {/* Caption bar */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              zIndex: 40, padding: '0 20px 20px',
              pointerEvents: 'none',
            }}>
              {/* Chapter label */}
              <div style={{
                color: 'rgba(100,160,255,0.75)', fontSize: 10, fontWeight: 700,
                letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 5,
              }}>
                {chapterLabel}
              </div>
              {/* Headline */}
              <div style={{
                color: '#fff', fontSize: 17, fontWeight: 700,
                lineHeight: 1.3, marginBottom: 7,
                textShadow: '0 2px 8px rgba(0,0,0,0.8)',
              }}>
                {headline}
              </div>
              {/* Script text */}
              <div style={{
                color: 'rgba(220,230,255,0.88)', fontSize: 12, lineHeight: 1.65,
                maxWidth: 680, textShadow: '0 1px 4px rgba(0,0,0,0.9)',
              }}>
                {text}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 3, zIndex: 41, background: 'rgba(255,255,255,0.08)',
              pointerEvents: 'none',
            }}>
              <div style={{
                height: '100%',
                width: `${totalChapters > 1 ? (chapterIndex / (totalChapters - 1)) * 100 : 0}%`,
                background: 'linear-gradient(to right, #4488ff, #88bbff)',
                transition: 'width 0.5s ease',
              }} />
            </div>

            {/* Chapter dots */}
            <div style={{
              position: 'absolute', bottom: 8, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', gap: 6, zIndex: 42,
              pointerEvents: 'auto',
            }}>
              {Array.from({ length: totalChapters }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToChapter(i)}
                  title={i === 0 ? 'Introduction' : i === totalChapters - 1 ? 'Closing' : `Issue ${i}`}
                  style={{
                    width: i === chapterIndex ? 18 : 7,
                    height: 7, borderRadius: 4,
                    background: i === chapterIndex ? '#88bbff' : 'rgba(255,255,255,0.25)',
                    border: 'none', cursor: 'pointer', padding: 0,
                    transition: 'width 0.3s ease, background 0.3s ease',
                  }}
                />
              ))}
            </div>

            {/* Playback controls bar */}
            <div style={{
              position: 'absolute', top: 12, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(4,8,18,0.90)',
              border: '1px solid rgba(100,160,255,0.35)',
              borderRadius: 8, padding: '5px 12px',
              zIndex: 42, pointerEvents: 'auto',
            }}>
              {/* Chapter label in controls */}
              <span style={{ color: 'rgba(100,160,255,0.7)', fontSize: 10, letterSpacing: 0.8, marginRight: 4 }}>
                ■ PRESENTING
              </span>
              {/* Previous chapter */}
              <button
                onClick={() => chapterIndex > 0 && goToChapter(chapterIndex - 1)}
                disabled={chapterIndex === 0}
                style={{
                  background: 'none', border: 'none',
                  color: chapterIndex === 0 ? '#333' : '#88bbff',
                  cursor: chapterIndex === 0 ? 'default' : 'pointer',
                  fontSize: 16, padding: '2px 4px',
                }}
                title="Previous chapter"
              >
                ⏮
              </button>
              {/* Play / Pause */}
              <button
                onClick={handleTogglePlayPause}
                style={{
                  background: 'rgba(100,160,255,0.15)',
                  border: '1px solid rgba(100,160,255,0.4)',
                  color: '#88bbff', borderRadius: 5,
                  cursor: 'pointer', fontSize: 15, padding: '3px 10px',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
                title={presentationPhase === 'playing' ? 'Pause' : 'Play'}
              >
                {presentationPhase === 'playing' ? '⏸' : '▶'}
                {!audioReady && (
                  <span style={{ fontSize: 9, color: 'rgba(100,160,255,0.5)' }}>↻</span>
                )}
              </button>
              {/* Next chapter */}
              <button
                onClick={() => chapterIndex < totalChapters - 1 && goToChapter(chapterIndex + 1)}
                disabled={chapterIndex === totalChapters - 1}
                style={{
                  background: 'none', border: 'none',
                  color: chapterIndex === totalChapters - 1 ? '#333' : '#88bbff',
                  cursor: chapterIndex === totalChapters - 1 ? 'default' : 'pointer',
                  fontSize: 16, padding: '2px 4px',
                }}
                title="Next chapter"
              >
                ⏭
              </button>
              {/* Divider */}
              <span style={{ color: '#333', fontSize: 14 }}>|</span>
              {/* Exit */}
              <button
                onClick={handleExitPresentation}
                style={{
                  background: 'none', border: 'none',
                  color: '#ff6666', cursor: 'pointer',
                  fontSize: 11, padding: '2px 4px', fontWeight: 700,
                }}
                title="Exit Presentation Mode"
              >
                ✕ Exit
              </button>
            </div>
          </>
        );
      })()}

      {/* CSS keyframes for loading spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IssueTerrain3D;
